import { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Spinner,
  Tooltip,
  Progress,
} from "@heroui/react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { emailEditorModules } from "shared/config/quillSetup";
import { validateFileForUpload, formatFileSize as sharedFormatFileSize, FILE_INPUT_ACCEPT } from "shared/utils/fileValidation";
import { MagnifyingGlass, Building, EnvelopeSimple, Phone, XCircle, UsersThree, DotsSixVertical, Plus, Database, Warning, Paperclip, UploadSimple, File, Trash } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import DirectSearchComponent from "shared/components/searchBar/components/directSearchComponent";
import SearchComponent from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { applyFooterSender } from "screens/mass-mail/utils/footerSender";

interface EmailTemplate {
  id?: number;
  subject: string;
  content: string;
  templateType: string;
  label: string;
  createdAt?: string;
  updatedAt?: string;
}

interface TemplateType {
  id?: number;
  name: string;
  createdAt?: string;
}

interface EmailSettings {
  id?: number;
  headerHtml?: string;
  footerHtml?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ContactUnit {
  id: string;
  unitId: string;
  unitNumber: string;
  referencePlus: string;
  buildingId: string;
  buildingName: string;
}

interface Contact {
  id: string;
  name: string;
  resourceId: string;
  firstName: string;
  lastName: string;
  phoneMobile?: string;
  email?: string;
  units: ContactUnit[];
  // For backward compatibility and easy access to primary building
  buildingId?: string;
  buildingName?: string;
  unitId?: string;
  unitNumber?: string;
}

interface SelectedResource {
  id: string;
  resourceId: string;
  type: 'building' | 'unit';
  name: string;
  contactCount: number;
}

interface AttachedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  uploadProgress?: number;
  isUploading?: boolean;
  uploadError?: string;
  previewUrl?: string;
}

interface MassMailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const modules = emailEditorModules;

export const MassMailModal: React.FC<MassMailModalProps> = ({
  isOpen,
  onClose
}) => {
  const { session } = useSession();
  
  // Resource and contact state
  const [selectedResources, setSelectedResources] = useState<SelectedResource[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  
  // Template state
  const [templateTypes, setTemplateTypes] = useState<TemplateType[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedType, setSelectedType] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [filteredTemplates, setFilteredTemplates] = useState<EmailTemplate[]>([]);
  
  // Email content state
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [senderEmail, setSenderEmail] = useState("ella@yally.be");
  

  const [emailSettings, setEmailSettings] = useState<EmailSettings | null>(null);
  
  // Loading states
  const [isSending, setIsSending] = useState(false);
  
  // Email send results state
  const [emailSendResults, setEmailSendResults] = useState<any>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Preview contact selection
  const [selectedPreviewContactId, setSelectedPreviewContactId] = useState<string>("dummy");

  // Refs for drag and drop
  const quillRef = useRef<ReactQuill>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);

  // Add Entity functionality state
  const [showAddEntity, setShowAddEntity] = useState(false);
  const [mentionedEntities, setMentionedEntities] = useState<any[]>([]);

  // Parameter functionality state
  const [parameters, setParameters] = useState<Array<{
    id: string;
    name: string;
    placeholder: string;
    type: 'global' | 'individual';
  }>>([]);
  const [showCreateParameter, setShowCreateParameter] = useState(false);
  const [newParameterName, setNewParameterName] = useState('');
  const [newParameterType, setNewParameterType] = useState<'global' | 'individual'>('global');
  const [parameterValues, setParameterValues] = useState<{ [key: string]: string | { [contactId: string]: string } }>({});

  // File attachment state
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Dummy contact data for preview
  const dummyContact: Contact = {
    id: "dummy",
    name: "Jan Janssen (voorbeeld)",
    resourceId: "RES-2024-001",
    firstName: "Jan",
    lastName: "Janssen",
    phoneMobile: "+32 123 456 789",
    email: "jan.janssen@example.be",
    units: []
  };

  // Get the selected preview contact. Zodra er een échte ontvanger met e-mailadres
  // geselecteerd is, tonen we die in het voorbeeld — de voorbeeldpersoon "Jan Janssen"
  // is enkel een terugval zonder geldige ontvanger, anders lijkt het alsof de mail
  // naar hem vertrekt.
  const getSelectedPreviewContact = () => {
    if (selectedPreviewContactId !== "dummy") {
      const chosenContact = contacts.find(c => String(c.id) === String(selectedPreviewContactId));
      if (chosenContact) return chosenContact;
    }
    return contacts.find(hasValidEmail) ?? contacts[0] ?? dummyContact;
  };

  // Generate consistent color for building based on buildingId or contact
  const getBuildingColor = (contact: Contact): string => {
    // Use primary building ID for coloring (first unit's building or fallback)
    const buildingId = contact.buildingId || (contact.units.length > 0 ? contact.units[0].buildingId : undefined);
    
    if (!buildingId) return 'bg-gray-100 border-gray-300 text-gray-700'; // Default for individual contacts
    
    // Create a simple hash from buildingId to get consistent colors
    let hash = 0;
    for (let i = 0; i < buildingId.length; i++) {
      hash = buildingId.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const colors = [
      'bg-neutral-primary/5 border-neutral-primary/15 text-neutral-primary',
      'bg-moss-primary/8 border-moss-primary/20 text-moss-primary',
      'bg-neutral-secondary-mid/10 border-neutral-secondary-mid/25 text-neutral-secondary-mid',
      'bg-status-good/8 border-status-good/20 text-status-good',
      'bg-blue-100/60 border-blue-200/50 text-blue-700',
      'bg-purple-100/60 border-purple-200/50 text-purple-600',
      'bg-cyan-100/50 border-cyan-200/60 text-cyan-700',
      'bg-emerald-100/50 border-emerald-200/60 text-emerald-700'
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Check if contact has valid email
  const hasValidEmail = (contact: Contact): boolean => {
    const trimmedEmail = contact.email?.trim();
    return !!(trimmedEmail && trimmedEmail !== 'GSM-nummer onbekend');
  };

  // Function to auto-detect and create missing parameters from template content
  const autoDetectAndCreateParameters = () => {
    const content = emailSubject + ' ' + emailContent;
    const parameterPlaceholders = content.match(/\{(gl|ind)_[a-zA-Z0-9_]+\}/g) || [];
    const existingPlaceholders = new Set(parameters.map(p => p.placeholder));

    const newParameters: Array<{
      id: string;
      name: string;
      placeholder: string;
      type: 'global' | 'individual';
    }> = [];

    parameterPlaceholders.forEach(placeholder => {
      if (!existingPlaceholders.has(placeholder)) {
        // Extract type and name from placeholder
        const match = placeholder.match(/\{(gl|ind)_([a-zA-Z0-9_]+)\}/);
        if (match) {
          const [, prefix, sanitizedName] = match;
          const type = prefix === 'gl' ? 'global' : 'individual';
          const name = sanitizedName.replace(/_/g, ' '); // Convert back to readable format

          const newParameter = {
            id: `autodetected_${Date.now()}_${newParameters.length}`,
            name: name,
            placeholder: placeholder,
            type: type as 'global' | 'individual'
          };

          newParameters.push(newParameter);
          existingPlaceholders.add(placeholder);
        }
      }
    });

    if (newParameters.length > 0) {
      setParameters(prev => [...prev, ...newParameters]);

      // Initialize parameter values for auto-detected parameters
      const newParameterValues: { [key: string]: string | { [contactId: string]: string } } = {};
      newParameters.forEach(param => {
        if (param.type === 'global') {
          newParameterValues[param.id] = '';
        } else {
          const individualValues: { [contactId: string]: string } = {};
          contacts.forEach(contact => {
            individualValues[contact.id] = '';
          });
          if (contacts.length === 0) {
            individualValues['default'] = '';
          }
          newParameterValues[param.id] = individualValues;
        }
      });

      setParameterValues(prev => ({ ...prev, ...newParameterValues }));

      return newParameters.length;
    }

    return 0;
  };

  // Function to detect missing entity types and parameters from template content
  const getMissingEntityTypes = () => {
    const content = emailSubject + ' ' + emailContent;
    
    // Check for missing entity types
    const entityPlaceholders = content.match(/\{[a-zA-Z]+\d+\.[a-zA-Z]+\}/g) || [];
    const requiredEntityTypes = new Set<string>();
    const addedEntityTypes = new Set<string>();
    
    // Extract required entity types from placeholders
    entityPlaceholders.forEach(placeholder => {
      const match = placeholder.match(/\{([a-zA-Z]+)\d+\./);
      if (match) {
        requiredEntityTypes.add(match[1]);
      }
    });
    
    // Get added entity types
    mentionedEntities.forEach(entity => {
      addedEntityTypes.add(entity.type);
    });
    
    // Find missing entity types
    const missingEntityTypes = Array.from(requiredEntityTypes).filter(type => !addedEntityTypes.has(type));
    
    // Check for missing parameters (both gl_ and ind_ prefixed)
    const parameterPlaceholders = content.match(/\{(gl|ind)_[a-zA-Z0-9_]+\}/g) || [];
    const requiredParameterNames = new Set<string>();
    const addedParameterPlaceholders = new Set<string>();
    
    // Extract required parameter placeholders
    parameterPlaceholders.forEach(placeholder => {
      requiredParameterNames.add(placeholder);
    });
    
    // Get added parameter placeholders
    parameters.forEach(param => {
      addedParameterPlaceholders.add(param.placeholder);
    });
    
    // Find missing parameters
    const missingParameterNames = Array.from(requiredParameterNames).filter(placeholder => !addedParameterPlaceholders.has(placeholder));
    
    // Map entity types to Dutch names
    const entityTypeNames: { [key: string]: string } = {
      'company': 'Bedrijf',
      'building': 'Gebouw', 
      'unit': 'Unit',
      'helpdesk': 'Helpdesk Ticket',
      'task': 'Taak',
      'contract': 'Contract'
    };
    
    return {
      missingTypes: missingEntityTypes.map(type => entityTypeNames[type] || type),
      missingParameters: missingParameterNames, // Keep as placeholders for now
      requiredEntityTypes: Array.from(requiredEntityTypes),
      requiredParameterNames: Array.from(requiredParameterNames),
      hasEntityFields: requiredEntityTypes.size > 0,
      hasParameterFields: requiredParameterNames.size > 0,
      missingCount: missingEntityTypes.length + missingParameterNames.length
    };
  };

  // Detect which dynamic fields are being used in the content
  const getUsedDynamicFields = (): string[] => {
    const content = `${emailSubject} ${emailContent}`;
    const fields: string[] = [];
    
    // Contact placeholders
    const contactPlaceholders = ['{ResourceID}', '{VolledigeNaam}', '{Voornaam}', '{Achternaam}', '{MobielNummer}', '{Email}'];
    
    contactPlaceholders.forEach(placeholder => {
      if (content.includes(placeholder)) {
        fields.push(placeholder);
      }
    });
    
    // Entity placeholders
    const entityFieldGroups = getEntityDynamicFields;
    Object.values(entityFieldGroups).forEach((entityGroup: any) => {
      entityGroup.fields.forEach((field: any) => {
        if (content.includes(field.placeholder)) {
          fields.push(field.placeholder);
        }
      });
    });
    
    return fields;
  };

  // Check which fields are missing for a contact
  const getMissingFields = (contact: Contact): string[] => {
    const usedFields = getUsedDynamicFields();
    const missing: string[] = [];
    
    // Filter out entity fields (those containing dots like "company1.ID", "building1.naam")
    const contactFields = usedFields.filter(field => !field.includes('.'));
    
    contactFields.forEach(field => {
      const fieldName = field.replace(/[{}]/g, ''); // Remove braces
      let value = '';
      
      switch (fieldName) {
        case 'ResourceID':
          value = contact.resourceId || '';
          break;
        case 'VolledigeNaam':
          value = contact.name || '';
          break;
        case 'Voornaam':
          value = contact.firstName || '';
          break;
        case 'Achternaam':
          value = contact.lastName || '';
          break;
        case 'MobielNummer':
          value = contact.phoneMobile || '';
          break;
        case 'Email':
          value = contact.email || '';
          break;
      }
      
      if (!value || value === null || value.trim() === '' || value === 'GSM-nummer onbekend') {
        missing.push(field);
      }
    });
    
    return missing;
  };

  // Function to replace dynamic values in preview content (for HTML content)
  const replacePreviewDynamicValues = (content: string, isHtml: boolean = true): string => {
    let personalizedContent = content;
    const selectedContact = getSelectedPreviewContact();
    
    // Available dynamic values - Dutch placeholders (Contact fields)
    const contactDynamicValues = {
      '{ResourceID}': (selectedContact.resourceId?.toString() || '').trim(),
      '{VolledigeNaam}': (selectedContact.name?.toString() || '').trim(),
      '{Voornaam}': (selectedContact.firstName?.toString() || '').trim(),
      '{Achternaam}': (selectedContact.lastName?.toString() || '').trim(),
      '{MobielNummer}': selectedContact.phoneMobile?.trim() || '',
      '{Email}': selectedContact.email?.trim() || ''
    };

    // Replace contact dynamic values
    Object.entries(contactDynamicValues).forEach(([placeholder, value]) => {
      if (isHtml) {
        // For HTML content, add highlighting
        const highlightedValue = `<span class="bg-yellow-100 px-1 py-0.5 rounded-sm text-xs font-medium" title="Dynamisch veld: ${placeholder}">${value}</span>`;
        personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), highlightedValue);
      } else {
        // For plain text (like subject), just replace with value
        personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value || '');
      }
    });

    // First, collect all available entity placeholders to avoid double-processing
    const entityFieldGroups = getEntityDynamicFields;
    const availableEntityPlaceholders = new Set<string>();
    
    // Replace entity dynamic values and track which ones we replaced
    Object.values(entityFieldGroups).forEach((entityGroup: any) => {
      entityGroup.fields.forEach((field: any) => {
        const placeholder = field.placeholder;
        const value = field.value;
        
        // Track this placeholder as available
        availableEntityPlaceholders.add(placeholder);
        
        if (isHtml) {
          // For HTML content, add highlighting with entity styling
          const highlightedValue = `<span class="bg-green-100 px-1 py-0.5 rounded-sm text-xs font-medium border border-green-300" title="Entiteit veld: ${placeholder} (${field.entityName})">${value}</span>`;
          personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), highlightedValue);
        } else {
          // For plain text (like subject), just replace with value
          personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value || '');
        }
      });
    });

    // Replace parameter dynamic values
    parameters.forEach((parameter) => {
      const placeholder = parameter.placeholder;
      let parameterValue = '';
      
      if (parameter.type === 'global') {
        parameterValue = parameterValues[parameter.id] as string || `[${parameter.name}]`;
      } else {
        const individualValues = parameterValues[parameter.id] as { [contactId: string]: string } || {};
        const selectedContact = getSelectedPreviewContact();
        parameterValue = individualValues[selectedContact.id] || individualValues['default'] || `[${parameter.name} voor ${selectedContact.name}]`;
      }
      
      if (isHtml) {
        // For HTML content, add highlighting with parameter styling
        const highlightedValue = `<span class="bg-purple-100 px-1 py-0.5 rounded-sm text-xs font-medium border border-purple-300" title="Variabel veld: ${placeholder} (${parameter.type === 'global' ? 'Globaal' : 'Individueel'})">${parameterValue}</span>`;
        personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), highlightedValue);
      } else {
        // For plain text (like subject), just replace with value
        personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), parameterValue);
      }
    });

    // Highlight unfilled parameter placeholders in red (for new format)
    if (isHtml) {
      const parameterPlaceholderRegex = /\{(gl|ind)_[a-zA-Z0-9_]+\}/g;
      personalizedContent = personalizedContent.replace(parameterPlaceholderRegex, (match) => {
        // Check if this parameter is already handled by our created parameters
        const isHandled = parameters.some(param => param.placeholder === match);
        if (!isHandled) {
          const paramMatch = match.match(/\{(gl|ind)_([a-zA-Z0-9_]+)\}/);
          const paramName = paramMatch ? paramMatch[2].replace(/_/g, ' ') : match;
          const paramType = paramMatch ? (paramMatch[1] === 'gl' ? 'Globaal' : 'Individueel') : '';
          return `<span class="bg-red-100 px-1 py-0.5 rounded-sm text-xs font-medium border border-red-300 text-red-700" title="Ontbrekende parameter: ${paramName} (${paramType})">${match}</span>`;
        }
        return match;
      });
    }

    // Highlight only unfilled entity placeholders in red (those not in availableEntityPlaceholders)
    if (isHtml) {
      const entityPlaceholderRegex = /\{[a-zA-Z]+\d+\.[a-zA-Z]+\}/g;
      personalizedContent = personalizedContent.replace(entityPlaceholderRegex, (match) => {
        // Only highlight if this placeholder wasn't already replaced by an available entity
        if (!availableEntityPlaceholders.has(match)) {
          return `<span class="bg-red-100 px-1 py-0.5 rounded-sm text-xs font-medium border border-red-300 text-red-700" title="Ontbrekende entiteit: ${match}">${match}</span>`;
        }
        return match; // Return unchanged if it was already processed
      });
    }

    return personalizedContent;
  };

  // Dynamic fields data for drag and drop
  const dynamicFields = [
    { key: 'firstName', label: 'Voornaam', placeholder: '{Voornaam}', value: getSelectedPreviewContact().firstName },
    { key: 'lastName', label: 'Achternaam', placeholder: '{Achternaam}', value: getSelectedPreviewContact().lastName },
    { key: 'name', label: 'Volledige naam', placeholder: '{VolledigeNaam}', value: getSelectedPreviewContact().name },
    { key: 'email', label: 'E-mailadres', placeholder: '{Email}', value: getSelectedPreviewContact().email },
    { key: 'phoneMobile', label: 'Mobiel nummer', placeholder: '{MobielNummer}', value: getSelectedPreviewContact().phoneMobile },
    { key: 'resourceId', label: 'Resource ID', placeholder: '{ResourceID}', value: getSelectedPreviewContact().resourceId }
  ];

  // Generate dynamic fields for mentioned entities
  const getEntityDynamicFields = useMemo(() => {
    const entityFieldGroups: { [key: string]: any } = {};
    
    mentionedEntities.forEach(entity => {
      const fields: any[] = [];
      
      // Only include enabled fields
      Object.entries(entity.fields || {}).forEach(([fieldName, fieldData]: [string, any]) => {
        if (fieldData.emailEnabled) {
          const field = {
            key: `${entity.uniqueId}.${fieldName}`,
            label: fieldData.dutchName || fieldName,
            placeholder: `{${entity.uniqueId}.${fieldData.dutchName || fieldName}}`,
            value: fieldData.value || 'Geen waarde',
            entityId: entity.uniqueId,
            entityName: entity.name,
            entityType: entity.dutchDisplayName
          };
          
          fields.push(field);
        }
      });
      
      if (fields.length > 0) {
        entityFieldGroups[entity.uniqueId] = {
          entityName: entity.name,
          entityType: entity.dutchDisplayName,
          fields: fields
        };
      }
    });
    
    return entityFieldGroups;
  }, [mentionedEntities]);

  // Auto-scroll functionality
  const autoScrollInterval = useRef<NodeJS.Timeout | null>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef<boolean>(false);
  
  const startAutoScroll = (element: HTMLElement, direction: 'up' | 'down') => {
    if (autoScrollInterval.current) return; // Already scrolling
    
    console.log('▶️ Starting auto-scroll:', direction);
    autoScrollInterval.current = setInterval(() => {
      const scrollAmount = direction === 'up' ? -15 : 15;
      const oldScrollTop = element.scrollTop;
      element.scrollTop += scrollAmount;
      console.log('📜 Scrolling:', direction, 'from', oldScrollTop, 'to', element.scrollTop);
    }, 40);
  };
  
  const stopAutoScroll = () => {
    if (autoScrollInterval.current) {
      console.log('⏹️ Stopping auto-scroll');
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }
  };
  
  const handleAutoScroll = (e: React.DragEvent, element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const y = e.clientY;
    const scrollZone = 120; // Even larger zone for easier scrolling
    
    if (y < rect.top + scrollZone) {
      startAutoScroll(element, 'up');
    } else if (y > rect.bottom - scrollZone) {
      startAutoScroll(element, 'down');
    } else {
      stopAutoScroll();
    }
  };

  // Handle modal-level auto-scroll when dragging
  const handleModalDragOver = (e: React.DragEvent) => {
    if (!modalBodyRef.current || !isDragging.current) return;
    
    e.preventDefault();
    
    const modalBody = modalBodyRef.current;
    const modalRect = modalBody.getBoundingClientRect();
    const y = e.clientY;
    const scrollZone = 150; // Zone for viewport-based scrolling
    
    // Use the visible viewport of the modal, not the entire modal content
    const viewportTop = modalRect.top;
    const viewportBottom = modalRect.bottom;
    const viewportHeight = viewportBottom - viewportTop;
    
    console.log('🔍 Modal drag over (viewport-based):', {
      mouseY: y,
      viewportTop,
      viewportBottom,
      viewportHeight,
      scrollZone,
      topTrigger: viewportTop + scrollZone,
      bottomTrigger: viewportBottom - scrollZone,
      isDragging: isDragging.current,
      currentScrollTop: modalBody.scrollTop
    });
    
    // Check if we're dragging near the visible edges of the viewport
    if (y < viewportTop + scrollZone) {
      console.log('🔼 Triggering UP scroll (viewport-based)');
      startAutoScroll(modalBody, 'up');
    } else if (y > viewportBottom - scrollZone) {
      console.log('🔽 Triggering DOWN scroll (viewport-based)');
      startAutoScroll(modalBody, 'down');
    } else {
      console.log('⏹️ Stopping scroll (in middle zone)');
      stopAutoScroll();
    }
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, placeholder: string) => {
    e.dataTransfer.setData('text/plain', placeholder);
    e.dataTransfer.effectAllowed = 'copy';
    isDragging.current = true;
    console.log('🚀 Drag started:', placeholder, 'isDragging:', isDragging.current);
  };

  // Handle drag end to stop auto-scroll
  const handleDragEnd = () => {
    isDragging.current = false;
    stopAutoScroll();
  };

  // Handle drop on subject input
  const handleSubjectDrop = (e: React.DragEvent) => {
    e.preventDefault();
    isDragging.current = false;
    stopAutoScroll();
    
    const placeholder = e.dataTransfer.getData('text/plain');
    if (placeholder && subjectInputRef.current) {
      const input = subjectInputRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const value = emailSubject;
      const newValue = value.substring(0, start) + placeholder + value.substring(end);
      setEmailSubject(newValue);
      
      // Set cursor position after the inserted text
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    }
  };

  // Handle drop on ReactQuill editor
  const handleEditorDrop = (e: React.DragEvent) => {
    e.preventDefault();
    isDragging.current = false;
    stopAutoScroll();
    
    const placeholder = e.dataTransfer.getData('text/plain');
    if (placeholder && quillRef.current) {
      const editor = quillRef.current.getEditor();
      
      // Get the exact position from drop coordinates
      const editorEl = editor.root;
      const rect = editorEl.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Try to find the exact position using browser APIs
      let insertIndex = editor.getLength() - 1; // fallback to end
      
      try {
        // Create a temporary range at the drop coordinates
        // Modern caret position API with fallback
        const getCaretPosition = (x: number, y: number) => {
          // Modern API (Firefox)
          if ((document as any).caretPositionFromPoint) {
            const caretPos = (document as any).caretPositionFromPoint(x, y);
            if (caretPos) {
              const range = document.createRange();
              range.setStart(caretPos.offsetNode, caretPos.offset);
              return range;
            }
          }
          // Fallback for older browsers (with proper typing)
          if ((document as any).caretRangeFromPoint) {
            return (document as any).caretRangeFromPoint(x, y);
          }
          return null;
        };
        
        const range = getCaretPosition(e.clientX, e.clientY);
        if (range) {
          if (range && editorEl.contains(range.startContainer)) {
            // Walk through the editor content to find the Quill index
            let walker = document.createTreeWalker(
              editorEl,
              NodeFilter.SHOW_TEXT,
              null
            );
            
            let textNode;
            let totalOffset = 0;
            while (textNode = walker.nextNode()) {
              if (textNode === range.startContainer) {
                insertIndex = totalOffset + range.startOffset;
                break;
              } else {
                totalOffset += textNode.textContent?.length || 0;
              }
            }
          }
        } 
        // Fallback for Firefox (with proper typing)
        else if ((document as any).caretPositionFromPoint) {
          const caretPos = (document as any).caretPositionFromPoint(e.clientX, e.clientY);
          if (caretPos && caretPos.offsetNode && editorEl.contains(caretPos.offsetNode)) {
            let walker = document.createTreeWalker(
              editorEl,
              NodeFilter.SHOW_TEXT,
              null
            );
            
            let textNode;
            let totalOffset = 0;
            while (textNode = walker.nextNode()) {
              if (textNode === caretPos.offsetNode) {
                insertIndex = totalOffset + caretPos.offset;
                break;
              } else {
                totalOffset += textNode.textContent?.length || 0;
              }
            }
          }
        }
        // Manual calculation fallback
        else {
          // Calculate approximate position based on coordinates
          const textLength = editor.getLength();
          const lineHeight = parseInt(getComputedStyle(editorEl).lineHeight) || 20;
          const charWidth = parseInt(getComputedStyle(editorEl).fontSize) * 0.6 || 10; // rough estimate
          
          const lineNumber = Math.floor(y / lineHeight);
          const charPosition = Math.floor(x / charWidth);
          
          // Very rough estimation - this is the weakest fallback
          insertIndex = Math.min(lineNumber * 80 + charPosition, textLength - 1);
        }
        
        // Ensure we don't exceed bounds
        insertIndex = Math.max(0, Math.min(insertIndex, editor.getLength() - 1));
        
      } catch (error) {
        console.log('Position calculation failed, using end position');
        insertIndex = editor.getLength() - 1;
      }
      
      // Insert the placeholder at the calculated position
      editor.insertText(insertIndex, placeholder);
      editor.setSelection(insertIndex + placeholder.length, 0);
    }
  };

  // Handle drag over subject input with auto-scroll
  const handleSubjectDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    e.stopPropagation(); // Prevent modal scrolling when over subject
    
    // Auto-scroll if dragging near edges
    if (subjectInputRef.current) {
      handleAutoScroll(e, subjectInputRef.current);
    }
  };

  // Handle drag over editor with auto-scroll
  const handleEditorDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    e.stopPropagation(); // Prevent modal scrolling when over editor
    
    // Auto-scroll if dragging near edges of the editor container
    const editorContainer = e.currentTarget as HTMLElement;
    if (editorContainer) {
      handleAutoScroll(e, editorContainer);
    }
  };

  // Handle drag leave to stop auto-scroll
  const handleDragLeave = (e: React.DragEvent) => {
    // Only stop scrolling if we're leaving the container entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      stopAutoScroll();
    }
  };

  // File attachment handlers — use shared utility
  const formatFileSize = sharedFormatFileSize;

  const handleFileSelect = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const currentTotalSize = attachedFiles.reduce((total, f) => total + f.size, 0);

    // Validate files using shared utility
    const validFiles: File[] = [];
    const errors: string[] = [];
    let runningTotal = currentTotalSize;

    for (const file of fileArray) {
      const error = validateFileForUpload(file, runningTotal);
      if (error) {
        errors.push(error);
        continue;
      }
      runningTotal += file.size;
      
      // Check if file already exists
      if (attachedFiles.some(af => af.name === file.name && af.size === file.size)) {
        errors.push(`${file.name}: Bestand al toegevoegd`);
        continue;
      }
      
      validFiles.push(file);
    }

    if (errors.length > 0) {
      toast.warning(errors.join('\n'));
    }
    
    if (validFiles.length === 0) return;
    
    // Create attachment objects
    const newAttachments: AttachedFile[] = validFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      isUploading: false,
      uploadProgress: 0,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
    }));
    
    setAttachedFiles(prev => [...prev, ...newAttachments]);
    toast.success(`${validFiles.length} bestand(en) toegevoegd`);
  };

  const handleRemoveFile = (fileId: string) => {
    setAttachedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const getTotalAttachmentSize = (): number => {
    return attachedFiles.reduce((sum, file) => sum + file.size, 0);
  };

  useEffect(() => {
    if (isOpen) {
      fetchTemplateTypes();
      fetchTemplates();
      fetchEmailSettings();
    } else {
      // Stop auto-scroll when modal closes
      stopAutoScroll();
    }
  }, [isOpen]);

  // Cleanup auto-scroll on unmount
  useEffect(() => {
    return () => {
      stopAutoScroll();
    };
  }, []);

  useEffect(() => {
    // Filter templates based on selected type
    if (selectedType) {
      const filtered = templates.filter(template => template.templateType === selectedType);
      setFilteredTemplates(filtered);
    } else {
      setFilteredTemplates([]);
    }
    setSelectedTemplate("");
    setEmailSubject("");
    setEmailContent("");
  }, [selectedType, templates]);

  useEffect(() => {
    // Load selected template content
    if (selectedTemplate) {
      const template = templates.find(t => t.id?.toString() === selectedTemplate);
      if (template) {
        setEmailSubject(template.subject);
        setEmailContent(template.content);
      }
    }
  }, [selectedTemplate, templates]);

  // Function to get required entity instances from template content
  const getRequiredEntityInstances = () => {
    const content = emailSubject + ' ' + emailContent;
    const entityPlaceholders = content.match(/\{[a-zA-Z]+\d+\.[a-zA-Z]+\}/g) || [];
    const requiredEntityInstances = new Set<string>();

    entityPlaceholders.forEach(placeholder => {
      const match = placeholder.match(/\{([a-zA-Z]+\d+)\./);
      if (match) {
        requiredEntityInstances.add(match[1]); // This captures "company1", "company2", etc.
      }
    });

    return Array.from(requiredEntityInstances);
  };

  // Auto-detect parameters when content changes
  useEffect(() => {
    if (emailSubject || emailContent) {
      const detectedCount = autoDetectAndCreateParameters();
      if (detectedCount > 0) {
        console.log(`Auto-detected ${detectedCount} parameters from template content`);
      }
    }
  }, [emailSubject, emailContent, contacts]);

  const fetchTemplateTypes = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/types`,
        { method: "GET" },
        session
      );
      
      if (response.ok) {
        const fetchedTypes = await response.json();
        setTemplateTypes(fetchedTypes);
      }
    } catch (error) {
      console.error("Error fetching template types:", error);
      toast.error("Fout bij het ophalen van sjabloontypen");
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates`,
        { method: "GET" },
        session
      );
      
      if (response.ok) {
        const fetchedTemplates = await response.json();
        setTemplates(fetchedTemplates);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Fout bij het ophalen van sjablonen");
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/settings`,
        { method: "GET" },
        session
      );
      
      if (response.ok) {
        const settings = await response.json();
        setEmailSettings(settings);
      }
    } catch (error) {
      console.error("Error fetching email settings:", error);
    }
  };

  const fetchBuildingContacts = async (buildingId: string): Promise<Contact[]> => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/building/${buildingId}/active`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        // CLOSED AND CONCEPT CONTRACTS ARE FILTERED OUT IN THE BACKEND

        const activeContracts = await response.json();
        const contactsMap = new Map<string, Contact>();

        activeContracts.forEach((contract: any) => {
          contract.contractContacts.forEach((contactRelation: any) => {
            if (contactRelation && contactRelation.resource) {
              const contactId = contactRelation.resource.id;
              const unitInfo: ContactUnit = {
                id: contract.unit?.id || '',
                unitId: contract.unit?.id || '',
                unitNumber: contract.unit?.boxNumber || contract.unit?.id || '',
                referencePlus: contract.unit?.referencePlus || '',
                buildingId: contract.unit?.building?.id || '',
                buildingName: contract.unit?.building?.location?.formattedAddress || `Gebouw ${contract.unit?.building?.id}` || '',
              };

              if (contactsMap.has(contactId)) {
                // Add unit to existing contact
                const existingContact = contactsMap.get(contactId)!;
                // Check if this unit is already added to avoid duplicates
                if (!existingContact.units.some(u => u.unitId === unitInfo.unitId)) {
                  existingContact.units.push(unitInfo);
                }
              } else {
                // Create new contact
                const newContact: Contact = {
                  id: contactRelation.resource.id,
                  firstName: contactRelation.relatedContact.firstName,
                  lastName: contactRelation.relatedContact.lastName,
                  name: contactRelation.resource.name,
                  resourceId: (contactRelation.resourceId?.toString() || ''),
                  phoneMobile: contactRelation.resource.metadata1,
                  email: contactRelation.relatedContact.email,
                  units: [unitInfo],
                  // Set primary building/unit info (first one)
                  buildingId: unitInfo.buildingId,
                  buildingName: unitInfo.buildingName,
                  unitId: unitInfo.unitId,
                  unitNumber: unitInfo.unitNumber,
                };
                contactsMap.set(contactId, newContact);
              }
            }
          });
        });

        const uniqueContacts = Array.from(contactsMap.values());

        return uniqueContacts;
      }
    } catch (error) {
      console.error("Error fetching building contacts:", error);
      toast.error("Fout bij het ophalen van contactpersonen voor gebouw");
    }
    return [];
  };

  const fetchUnitContacts = async (unitId: string): Promise<Contact[]> => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/unit/${unitId}/active`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        // CLOSED AND CONCEPT CONTRACTS ARE FILTERED OUT IN THE BACKEND

        const activeContracts = await response.json();
        const contactsMap = new Map<string, Contact>();

        activeContracts.forEach((contract: any) => {
          contract.contractContacts.forEach((contactRelation: any) => {
            if (contactRelation && contactRelation.resource) {
              const contactId = contactRelation.resource.id;
              const unitInfo: ContactUnit = {
                id: contract.unit?.id || '',
                unitId: contract.unit?.id || '',
                unitNumber: contract.unit?.boxNumber || contract.unit?.id || '',
                referencePlus: contract.unit?.referencePlus || '',
                buildingId: contract.unit?.building?.id || '',
                buildingName: contract.unit?.building?.location?.formattedAddress || `Gebouw ${contract.unit?.building?.id}` || '',
              };

              if (contactsMap.has(contactId)) {
                // Add unit to existing contact
                const existingContact = contactsMap.get(contactId)!;
                // Check if this unit is already added to avoid duplicates
                if (!existingContact.units.some(u => u.unitId === unitInfo.unitId)) {
                  existingContact.units.push(unitInfo);
                }
              } else {
                // Create new contact
                const newContact: Contact = {
                  id: contactRelation.resource.id,
                  firstName: contactRelation.relatedContact.firstName,
                  lastName: contactRelation.relatedContact.lastName,
                  name: contactRelation.resource.name,
                  resourceId: (contactRelation.resourceId?.toString() || ''),
                  phoneMobile: contactRelation.resource.metadata1,
                  email: contactRelation.relatedContact.email,
                  units: [unitInfo],
                  // Set primary building/unit info (first one)
                  buildingId: unitInfo.buildingId,
                  buildingName: unitInfo.buildingName,
                  unitId: unitInfo.unitId,
                  unitNumber: unitInfo.unitNumber,
                };
                contactsMap.set(contactId, newContact);
              }
            }
          });
        });

        const uniqueContacts = Array.from(contactsMap.values());

        return uniqueContacts;
      }
    } catch (error) {
      console.error("Error fetching unit contacts:", error);
      toast.error("Fout bij het ophalen van contactpersonen voor unit");
    }
    return [];
  };

  const handleAddParticipant = async (resource: any) => {
    setIsLoadingContacts(true);

    try {
    if (resource.type === ResourceType.BUILDING) {
        // Check if building is already selected
        const isAlreadySelected = selectedResources.some(r => r.resourceId === resource.resourceId && r.type === 'building');
        if (isAlreadySelected) {
          toast.info("Dit gebouw is al geselecteerd");
          return;
        }

        // Fetch contacts from building
        const buildingContacts = await fetchBuildingContacts(resource.resourceId);

        // Add building to selected resources
        const newSelectedResource: SelectedResource = {
          id: resource.id,
          resourceId: resource.resourceId,
          type: 'building',
          name: resource.name,
          contactCount: buildingContacts.length
        };
        setSelectedResources(prev => [...prev, newSelectedResource]);

        // Add contacts to existing contacts (avoiding duplicates)
        setContacts(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newContacts = buildingContacts.filter(c => !existingIds.has(c.id));
          return [...prev, ...newContacts];
        });

      } else if (resource.type === ResourceType.UNIT) {
        // Check if unit is already selected
        const isAlreadySelected = selectedResources.some(r => r.resourceId === resource.resourceId && r.type === 'unit');
        if (isAlreadySelected) {
          toast.info("Deze unit is al geselecteerd");
          return;
        }

        // Fetch contacts from unit
        const unitContacts = await fetchUnitContacts(resource.resourceId);

        // Add unit to selected resources
        const newSelectedResource: SelectedResource = {
          id: resource.id,
          resourceId: resource.resourceId,
          type: 'unit',
          name: resource.name,
          contactCount: unitContacts.length
        };
        setSelectedResources(prev => [...prev, newSelectedResource]);

        // Add contacts to existing contacts (avoiding duplicates)
        setContacts(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newContacts = unitContacts.filter(c => !existingIds.has(c.id));
          return [...prev, ...newContacts];
        });

    } else if (resource.type === ResourceType.CONTACT) {
        // Check if contact is already selected
        const isAlreadySelected = contacts.some(c => c.id === resource.id);
        if (isAlreadySelected) {
          toast.info("Dit contact is al geselecteerd");
          return;
        }

      const newContact: Contact = {
        id: resource.id,
        name: resource.name,
        resourceId: (resource.resourceId?.toString() || ''),
        firstName: resource.contactDetails?.firstName || "",
        lastName: resource.contactDetails?.lastName || "",
        phoneMobile: resource.metadata1 || resource.contactDetails?.phoneMobile || "GSM-nummer onbekend",
        email: resource.contactDetails?.email,
          units: [], // Individual contacts don't have units initially
          buildingId: undefined, // Individual contacts don't have building info
          buildingName: undefined,
          unitId: undefined,
          unitNumber: undefined,
        };
        setContacts(prev => [...prev, newContact]);
      }
    } catch (error) {
      console.error("Error adding participant:", error);
      toast.error("Fout bij het toevoegen van geadresseerde");
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const handleRemoveContact = (id: string) => {
    setContacts((prev) => prev.filter((c) => c.id !== id));
  };

  const handleRemoveResource = async (resourceToRemove: SelectedResource) => {
    // Remove the resource from selectedResources
    setSelectedResources(prev => prev.filter(r =>
      !(r.resourceId === resourceToRemove.resourceId && r.type === resourceToRemove.type)
    ));

    // Fetch contacts from the resource being removed and remove them from contacts
    let contactsToRemove: Contact[] = [];
    if (resourceToRemove.type === 'building') {
      contactsToRemove = await fetchBuildingContacts(resourceToRemove.resourceId);
    } else if (resourceToRemove.type === 'unit') {
      contactsToRemove = await fetchUnitContacts(resourceToRemove.resourceId);
    }

    if (contactsToRemove.length > 0) {
      const idsToRemove = new Set(contactsToRemove.map(c => c.id));
      setContacts(prev => prev.filter(c => !idsToRemove.has(c.id)));
    }
  };

  const handleSendMassEmail = async () => {
    // Building is no longer required - contacts can be selected independently
    
    if (contacts.length === 0) {
      toast.error("Geen contactpersonen gevonden");
      return;
    }
    
    if (!emailSubject.trim() || !emailContent.trim()) {
      toast.error("Onderwerp en inhoud zijn verplicht");
      return;
    }

    // Get contacts with valid email addresses
    const validContacts = contacts.filter(c => hasValidEmail(c));
    
    if (validContacts.length === 0) {
      toast.error("Geen contactpersonen met geldig e-mailadres gevonden");
      return;
    }

    setIsSending(true);
    try {
      // Upload attachments first if any exist
      let uploadedAttachments: any[] = [];
      if (attachedFiles.length > 0) {
        console.log(`📎 Uploading ${attachedFiles.length} attachments...`);
        
        const formData = new FormData();
        attachedFiles.forEach(attachedFile => {
          formData.append('files', attachedFile.file);
        });

        const uploadResponse = await fetchWithAuth(
          `${API_URL}/email/upload-attachments`,
          {
            method: "POST",
            body: formData, // Don't set Content-Type header, let browser set it with boundary
          },
          session
        );

        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          uploadedAttachments = uploadResult.attachments;
          console.log(`✅ Successfully uploaded ${uploadedAttachments.length} attachments`);
        } else {
          const errorData = await uploadResponse.json();
          console.error("Attachment upload failed:", errorData);
          toast.error(`Fout bij uploaden bijlagen: ${errorData.message || 'Onbekende fout'}`);
          return;
        }
      }
      // Process dynamic fields for each contact
      const processedRecipients = validContacts.map((contact) => {
        // Replace contact dynamic fields
        let processedSubject = emailSubject;
        let processedContent = emailContent;
        
        // Contact field replacements
        const contactReplacements = {
          '{ResourceID}': (contact.resourceId?.toString() || '').trim(),
          '{VolledigeNaam}': (contact.name?.toString() || '').trim(),
          '{Voornaam}': (contact.firstName?.toString() || '').trim(),
          '{Achternaam}': (contact.lastName?.toString() || '').trim(),
          '{MobielNummer}': (contact.phoneMobile?.toString() || '').trim(),
          '{Email}': (contact.email?.toString() || '').trim()
        };
        
        Object.entries(contactReplacements).forEach(([placeholder, value]) => {
          const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
          processedSubject = processedSubject.replace(regex, value);
          processedContent = processedContent.replace(regex, value);
        });
        
        // Entity field replacements
        const entityFieldGroups = getEntityDynamicFields;
        Object.values(entityFieldGroups).forEach((entityGroup: any) => {
          entityGroup.fields.forEach((field: any) => {
            const regex = new RegExp(field.placeholder.replace(/[{}]/g, '\\$&'), 'g');
            processedSubject = processedSubject.replace(regex, field.value || '');
            processedContent = processedContent.replace(regex, field.value || '');
          });
        });

        // Parameter field replacements (supporting new gl_/ind_ format)
        parameters.forEach((parameter) => {
          const regex = new RegExp(parameter.placeholder.replace(/[{}]/g, '\\$&'), 'g');
          let parameterValue = '';
          
          if (parameter.type === 'global') {
            parameterValue = parameterValues[parameter.id] as string || '';
          } else {
            const individualValues = parameterValues[parameter.id] as { [contactId: string]: string } || {};
            parameterValue = individualValues[contact.id] || individualValues['default'] || '';
          }
          
          processedSubject = processedSubject.replace(regex, parameterValue);
          processedContent = processedContent.replace(regex, parameterValue);
        });
        
        // Combine header, content, and footer (preserve HTML formatting).
        // Het contactadres in de standaardfooter volgt de gekozen afzender
        // (screens/mass-mail/utils/footerSender.ts).
        const fullEmailContent = [
          emailSettings?.headerHtml || '',
          processedContent,
          applyFooterSender(emailSettings?.footerHtml, senderEmail)
        ].filter(Boolean).join('');
        
        // Fix links to ensure they have proper protocols for email clients
        const fixedEmailContent = fullEmailContent.replace(
          /href="([^"]+)"/g, 
          (match, url) => {
            // Only add https:// if the URL doesn't already have a protocol
            if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('mailto:') && !url.startsWith('#') && !url.startsWith('/')) {
              return `href="https://${url}"`;
            }
            return match;
          }
        );
        
        // Debug logging for HTML content
        console.log('🔍 Email content processing for:', contact.name);
        console.log('📝 Original emailContent:', emailContent);
        console.log('🔄 Processed content:', processedContent);
        console.log('📧 Full email content:', fullEmailContent);
        console.log('🔗 Fixed email content:', fixedEmailContent);
        
        // Debug specific href matches
        const hrefMatches = fullEmailContent.match(/href="[^"]+"/g);
        if (hrefMatches) {
          console.log('🔗 Found href attributes:', hrefMatches);
          hrefMatches.forEach(match => {
            const urlMatch = match.match(/href="([^"]+)"/);
            if (urlMatch) {
              const url = urlMatch[1];
              console.log(`🔗 Processing URL: "${url}"`);
            }
          });
        }
        
        return {
          emailAddress: {
            address: contact.email?.trim() || '',
            name: contact.name?.trim() || ''
          },
          subject: processedSubject,
          content: fixedEmailContent,
          // Keep original contact data for logging
          contactId: contact.id,
          contactName: contact.name
        };
      });


      // Debug: Check request size
      const requestBody = {
        senderEmail: senderEmail,
        processedRecipients: processedRecipients,
        buildingId: null, // No single building - multiple resources supported
        attachments: uploadedAttachments, // Include uploaded attachments
        metadata: {
          selectedResources: selectedResources.map(r => `${r.type}: ${r.name}`).join(', ') || "Individual contacts",
          totalContacts: contacts.length,
          validContacts: validContacts.length,
          mentionedEntities: mentionedEntities.length,
          attachmentCount: uploadedAttachments.length
        }
      };
      
      const requestBodySize = new Blob([JSON.stringify(requestBody)]).size;
      const avgSizePerContact = requestBodySize / validContacts.length;
      console.log(`📧 Mass email request size: ${(requestBodySize / 1024).toFixed(2)} KB for ${validContacts.length} contacts`);
      console.log(`📊 Average size per contact: ${(avgSizePerContact / 1024).toFixed(2)} KB`);
      
      if (avgSizePerContact > 50 * 1024) { // >50KB per contact
        console.warn(`⚠️ Large email content detected (${(avgSizePerContact / 1024).toFixed(2)} KB per contact). Consider reducing template size or using smaller batches.`);
      }
      
      // Implement batching for large requests (>500KB or >3 contacts) - Conservative batching for Graph API limits
      let BATCH_SIZE = 3; // Very conservative default batch size to avoid Microsoft Graph throttling
      const MAX_REQUEST_SIZE = 500 * 1024; // 500KB limit (reduced due to server limits)
      
      // Adjust batch size based on average content size per contact
      if (avgSizePerContact > 100 * 1024) { // >100KB per contact
        BATCH_SIZE = 1; // Send one-by-one for very large content
        console.warn(`📦 Using individual sending (${BATCH_SIZE} contact) due to very large email content`);
      } else if (avgSizePerContact > 50 * 1024) { // >50KB per contact
        BATCH_SIZE = 2; // Very small batches for moderately large content
        console.warn(`📦 Using micro-batches (${BATCH_SIZE} contacts) due to large email content`);
      }
      
      console.log(`📊 Final batch size: ${BATCH_SIZE} contacts per batch`);
      
      if (requestBodySize > MAX_REQUEST_SIZE || validContacts.length > BATCH_SIZE) {
        const totalBatches = Math.ceil(processedRecipients.length / BATCH_SIZE);
        const estimatedTime = Math.ceil(totalBatches * 3.5); // ~3.5 seconds per batch (including retry time)
        
        console.log(`📦 Using batched sending: ${totalBatches} batches (estimated ${estimatedTime}s)`);
        
        // Show user info about batching
        if (validContacts.length > 10) {
          toast.info(`Mass-mail wordt verzonden in ${totalBatches} batches om Microsoft Graph limieten te respecteren. Geschatte tijd: ${Math.ceil(estimatedTime/60)} minuten.`, { autoClose: 5000 });
        }
        
        // Split into batches
        const batches = [];
        for (let i = 0; i < processedRecipients.length; i += BATCH_SIZE) {
          batches.push(processedRecipients.slice(i, i + BATCH_SIZE));
        }
        
        let totalSent = 0;
        let failedBatches = 0;
        
        // Send each batch with retry logic for throttling
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          const batchRequestBody = {
            ...requestBody,
            processedRecipients: batch,
            metadata: {
              ...requestBody.metadata,
              batchInfo: `Batch ${i + 1}/${batches.length} (${batch.length} contacts)`,
              totalBatches: batches.length,
              currentBatch: i + 1
            }
          };
          
          let batchSent = false;
          let retryAttempt = 0;
          const maxRetries = 3;
          
          while (!batchSent && retryAttempt <= maxRetries) {
            try {
              const retryText = retryAttempt > 0 ? ` (Poging ${retryAttempt + 1}/${maxRetries + 1})` : '';
              console.log(`📤 Sending batch ${i + 1}/${batches.length} with ${batch.length} contacts${retryText}`);
              
              // Update UI to show batch progress
              toast.info(`Verzenden batch ${i + 1}/${batches.length}${retryText}...`, { autoClose: 2000 });
              
              const batchResponse = await fetchWithAuth(
                `${API_URL}/email/send-mass-email`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(batchRequestBody),
                },
                session
              );
              
              if (batchResponse.ok) {
                await batchResponse.json();
                totalSent += batch.length;
                console.log(`✅ Batch ${i + 1} sent successfully (${batch.length} contacts)`);
                batchSent = true;
              } else if (batchResponse.status === 429) {
                // Microsoft Graph API rate limit hit - wait longer before retry
                const waitTime = Math.min(5000 * Math.pow(2, retryAttempt), 30000); // Exponential backoff, max 30s
                const responseText = await batchResponse.text();
                
                console.warn(`⏳ Microsoft Graph rate limit hit for batch ${i + 1}. Response: ${responseText}`);
                console.warn(`⏳ Waiting ${waitTime/1000}s before retry (attempt ${retryAttempt + 1}/${maxRetries + 1})...`);
                
                // Show user-friendly message
                if (responseText.includes('MailboxConcurrency')) {
                  toast.warning(`Microsoft Graph API limiet bereikt (te veel gelijktijdige emails). Wacht ${waitTime/1000}s...`, { autoClose: waitTime });
                } else {
                  toast.warning(`Rate limit bereikt. Wacht ${waitTime/1000}s...`, { autoClose: waitTime });
                }
                
                await new Promise(resolve => setTimeout(resolve, waitTime));
                retryAttempt++;
              } else {
                // Other error - don't retry
                failedBatches++;
                console.error(`❌ Batch ${i + 1} failed with status ${batchResponse.status}:`, await batchResponse.text());
                batchSent = true; // Stop trying this batch
              }
            } catch (error: unknown) {
              console.error(`❌ Batch ${i + 1} error (attempt ${retryAttempt + 1}):`, error);
              
              // Check if it's a network/timeout error that might benefit from retry
              const errorMessage = error instanceof Error ? error.message : String(error);
              const errorName = error instanceof Error ? error.name : 'Unknown';
              
              if (retryAttempt < maxRetries && (errorName === 'TypeError' || errorMessage.includes('fetch'))) {
                const waitTime = 3000 * (retryAttempt + 1);
                console.warn(`🔄 Retrying batch ${i + 1} in ${waitTime/1000}s due to network error...`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
                retryAttempt++;
              } else {
                failedBatches++;
                batchSent = true; // Stop trying this batch
              }
            }
          }
          
          // Longer delay between batches to avoid Microsoft Graph throttling
          if (i < batches.length - 1) {
            const delayTime = 3000; // Increased to 3 seconds
            console.log(`⏳ Waiting ${delayTime/1000}s before next batch to avoid throttling...`);
            await new Promise(resolve => setTimeout(resolve, delayTime));
          }
        }
        
        // Create consolidated results for batch sending
        const batchResults = {
          success: failedBatches === 0,
          totalRecipients: processedRecipients.length,
          successfulSends: totalSent,
          failedSends: processedRecipients.length - totalSent,
          senderEmail: senderEmail,
          timestamp: new Date().toISOString(),
          emailType: 'batch_send',
          buildingId: null,
          attachments: uploadedAttachments, // Store attachments for potential retry
          results: processedRecipients.map((recipient, index) => ({
            success: index < totalSent, // Simplified - assumes first N were successful
            recipient: {
              contactId: recipient.contactId,
              contactName: recipient.contactName,
              emailAddress: recipient.emailAddress.address
            },
            error: index >= totalSent ? 'Batch send failed' : null
          }))
        };
        
        if (failedBatches === 0) {
          toast.success(`🎉 Mass-mail succesvol verzonden naar ${totalSent} contactpersonen in ${batches.length} batches!`);
          console.log(`📊 Success: All ${batches.length} batches sent successfully`);
        } else if (totalSent > 0) {
          const successRate = ((batches.length - failedBatches) / batches.length * 100).toFixed(0);
          toast.warning(`⚠️ Mass-mail gedeeltelijk verzonden: ${totalSent} contacten succesvol (${successRate}% success rate), ${failedBatches} batches gefaald`);
          console.log(`📊 Partial success: ${batches.length - failedBatches}/${batches.length} batches sent successfully`);
        } else {
          toast.error(`❌ Alle batches gefaald bij verzenden mass-mail. Mogelijk Microsoft Graph API problemen.`);
          console.error(`📊 Complete failure: 0/${batches.length} batches sent successfully`);
          return;
        }
        
        // Set results to show the results modal
        setEmailSendResults(batchResults);
      } else {
        // Send single request for small payloads
        const response = await fetchWithAuth(
          `${API_URL}/email/send-mass-email`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          },
          session
        );

        if (response.ok) {
          const singleSendResult = await response.json();
          // Add attachments to the result for potential retry
          singleSendResult.attachments = uploadedAttachments;
          
          toast.success(`Mass-mail succesvol verzonden naar ${validContacts.length} contactpersonen!`);
          
          // Set results to show the results modal
          setEmailSendResults(singleSendResult);
        } else {
          const errorData = await response.json();
          console.error("Server error:", errorData);
          toast.error(`Fout bij verzenden: ${errorData.message || 'Onbekende fout'}`);
          return;
        }
      }
      
      // Log successful send for debugging
      console.log("Mass email sent successfully:", {
        selectedResources: selectedResources.map(r => `${r.type}: ${r.name}`).join(', ') || "Individual contacts",
        sender: senderEmail,
        totalRecipients: validContacts.length,
        subject: emailSubject
      });
      
      // Don't call handleClose() here - let the results modal show first
    } catch (error) {
      console.error("Error sending mass email:", error);
      toast.error("Fout bij verzenden van mass-mail");
    } finally {
      setIsSending(false);
    }
  };

  // Transform email search results to match SearchComponent format
  const transformEmailSearchResults = (data: any[]) => {
    if (!data || data.length === 0) {
      return [];
    }
    
    return data.map((item: any) => ({
      id: item.id,
      resourceId: (item.id?.toString() || ''),
      type: item.entityType,
      name: item.name,
      metadata1: item.dutchDisplayName,
      metadata2: Object.keys(item.fields || {}).length + " velden beschikbaar",
      entityData: item // Store the full entity data for later use
    }));
  };

  // Handle adding a mentioned entity
  const handleAddMentionedEntity = async (resource: any) => {
    try {
      console.log('🔍 Adding entity data for:', resource);
      
      // Check if we already have the entity data from the search
      if (resource.entityData && resource.entityData.fields) {
        console.log('✅ Using existing entity data from search');
        const fullEntityData = resource.entityData;

        // Use the specific uniqueId if provided, otherwise generate one
        let uniqueId;
        if (resource.originalUniqueId) {
          uniqueId = resource.originalUniqueId;
        } else {
          uniqueId = `${resource.type}${mentionedEntities.filter(e => e.type === resource.type).length + 1}`;
        }
        
        const newEntity = {
          id: resource.id,
          type: resource.type,
          name: resource.name,
          entityData: fullEntityData,
          uniqueId: uniqueId,
          fields: fullEntityData.fields || {},
          dutchDisplayName: fullEntityData.dutchDisplayName || resource.type
        };
        
        console.log(`✅ Added entity ${newEntity.uniqueId}: ${newEntity.name} (${Object.keys(fullEntityData.fields || {}).length} fields)`);
        
        setMentionedEntities(prev => [...prev, newEntity]);
        setShowAddEntity(false);
        return;
      }
      
      // Fallback: fetch full entity data if not available
      console.log('🔍 Fetching full entity data for:', resource);
      
      // Try direct entity fetch first (more reliable)
      const entityId = resource.resourceId || resource.id;
      let response = await fetchWithAuth(
        `${API_URL}/email/get-entity/${resource.type}/${entityId}`,
        { method: 'GET' },
        session
      );
      
      // If direct fetch fails, fall back to search
      if (!response.ok) {
        console.log('🔍 Direct fetch failed, trying search fallback');
        response = await fetchWithAuth(
          `${API_URL}/email/search-entities?query=${encodeURIComponent(entityId)}&entityTypes=${resource.type}`,
          { method: 'GET' },
          session
        );
      }

      if (response.ok) {
        const responseData = await response.json();
        console.log(`🔍 Fetching entity ${resource.type} ID:${entityId} - got ${Array.isArray(responseData) ? responseData.length + ' results' : 'single entity'}`);
        
        let fullEntityData;
        
        // Check if this is a direct fetch response (single object) or search response (array)
        if (Array.isArray(responseData)) {
          // Search response - find the matching entity
          
          fullEntityData = responseData.find((item: any) => {
            // Try multiple ID matching strategies
            const itemId = item.id?.toString();
            const resourceId = resource.id?.toString();
            const resourceResourceId = resource.resourceId?.toString();
            
            return itemId === resourceId || 
                   itemId === resourceResourceId ||
                   item.id === resource.id ||
                   item.id === resource.resourceId;
          });
        } else {
          // Direct fetch response - use the entity directly
          fullEntityData = responseData;
        }
        
        if (fullEntityData) {
          // Use the specific uniqueId if provided, otherwise generate one
          let uniqueId;
          if (resource.originalUniqueId) {
            uniqueId = resource.originalUniqueId;
          } else {
            uniqueId = `${resource.type}${mentionedEntities.filter(e => e.type === resource.type).length + 1}`;
          }

          const newEntity = {
            id: resource.id,
            type: resource.type,
            name: resource.name,
            entityData: fullEntityData,
            uniqueId: uniqueId,
            fields: fullEntityData.fields || {},
            dutchDisplayName: fullEntityData.dutchDisplayName || resource.type
          };
          
          console.log(`✅ Added entity ${newEntity.uniqueId}: ${newEntity.name} (${Object.keys(fullEntityData.fields || {}).length} fields)`);
          
          setMentionedEntities(prev => [...prev, newEntity]);
          setShowAddEntity(false);
        } else {
          toast.error("Kon entiteit gegevens niet ophalen");
        }
      } else {
        toast.error("Fout bij ophalen entiteit gegevens");
      }
    } catch (error) {
      console.error("Error fetching entity data:", error);
      toast.error("Fout bij ophalen entiteit gegevens");
    }
  };

  // Handle removing a mentioned entity
  const handleRemoveMentionedEntity = (uniqueId: string) => {
    setMentionedEntities(prev => prev.filter(entity => entity.uniqueId !== uniqueId));
  };

  // Parameter management functions
  const handleCreateParameter = () => {
    if (!newParameterName.trim()) return;
    
    const parameterNumber = parameters.length + 1;
    const sanitizedName = newParameterName.trim().replace(/\s+/g, '_').toLowerCase();
    const newParameter = {
      id: `param${parameterNumber}`,
      name: newParameterName.trim(),
      placeholder: `{parameter_${sanitizedName}}`,
      type: newParameterType
    };
    
    setParameters(prev => [...prev, newParameter]);
    setNewParameterName('');
    setShowCreateParameter(false);
    
    // Initialize parameter values
    if (newParameterType === 'global') {
      setParameterValues(prev => ({ ...prev, [newParameter.id]: '' }));
    } else {
      const individualValues: { [contactId: string]: string } = {};
      contacts.forEach(contact => {
        individualValues[contact.id] = '';
      });
      setParameterValues(prev => ({ ...prev, [newParameter.id]: individualValues }));
    }
  };

  const handleRemoveParameter = (parameterId: string) => {
    setParameters(prev => prev.filter(p => p.id !== parameterId));
    setParameterValues(prev => {
      const newValues = { ...prev };
      delete newValues[parameterId];
      return newValues;
    });
  };

  const handleParameterValueChange = (parameterId: string, value: string, contactId?: string) => {
    setParameterValues(prev => {
      const parameter = parameters.find(p => p.id === parameterId);
      if (!parameter) return prev;
      
      if (parameter.type === 'global') {
        return { ...prev, [parameterId]: value };
      } else if (contactId) {
        const currentValues = prev[parameterId] as { [contactId: string]: string } || {};
        return { ...prev, [parameterId]: { ...currentValues, [contactId]: value } };
      }
      return prev;
    });
  };

  const handleRetryFailedEmails = async () => {
    if (!emailSendResults || !emailSendResults.failedRecipients?.length) {
      return;
    }

    setIsRetrying(true);
    try {
      // Add phone numbers from contacts to failed recipients
      const failedRecipientsWithPhone = emailSendResults.failedRecipients.map((failedRecipient: any) => {
        const originalContact = contacts.find(c => c.id === failedRecipient.contactId);
        return {
          ...failedRecipient,
          phoneMobile: originalContact?.phoneMobile || "Niet beschikbaar"
        };
      });

      console.log("Retrying failed emails:", failedRecipientsWithPhone.length);
      
      const response = await fetchWithAuth(
        `${API_URL}/email/retry-failed-emails`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            senderEmail: emailSendResults.senderEmail,
            failedRecipients: failedRecipientsWithPhone,
            buildingId: emailSendResults.buildingId,
            attachments: emailSendResults.attachments || [] // Include attachments from original send
          }),
        },
        session
      );

      if (response.ok) {
        const retryResult = await response.json();
        console.log("Retry result:", retryResult);
        
        if (retryResult.success && retryResult.stillFailed === 0) {
          toast.success(`Alle ${retryResult.successfulRetries} mislukte e-mails succesvol opnieuw verzonden!`);
          setEmailSendResults(null); // Close results modal
          handleClose(); // Reset all state - we're completely done!
        } else if (retryResult.successfulRetries > 0 && retryResult.stillFailed > 0) {
          toast.warning(`${retryResult.successfulRetries} e-mails succesvol opnieuw verzonden, ${retryResult.stillFailed} nog steeds mislukt.`);
          // Update the results with new retry data - keep modal open for remaining failures
          setEmailSendResults(retryResult);
        } else {
          toast.error(`Alle ${retryResult.stillFailed} e-mails zijn nog steeds mislukt bij opnieuw proberen.`);
          setEmailSendResults(retryResult);
        }
      } else {
        const errorData = await response.json();
        console.error("Retry error:", errorData);
        toast.error(`Fout bij opnieuw proberen: ${errorData.message || 'Onbekende fout'}`);
      }
    } catch (error) {
      console.error("Error retrying failed emails:", error);
      toast.error("Fout bij opnieuw proberen van mislukte e-mails");
    } finally {
      setIsRetrying(false);
    }
  };

  const handleClose = () => {
    // Clean up file URLs
    attachedFiles.forEach(file => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
    
    setSelectedResources([]);
    setContacts([]);
    setSelectedType("");
    setSelectedTemplate("");
    setEmailSubject("");
    setEmailContent("");
    setSenderEmail("ella@yally.be");
    setSelectedPreviewContactId("dummy");
    setShowAddEntity(false);
    setMentionedEntities([]);
    setParameters([]);
    setShowCreateParameter(false);
    setNewParameterName('');
    setNewParameterType('global');
    setParameterValues({});
    setAttachedFiles([]);
    setEmailSendResults(null); // Clear results
    onClose();
  };

  return (
    <>
      {/* Email Send Results Modal */}
      {emailSendResults && (
        <Modal 
          isOpen={!!emailSendResults} 
          onClose={() => {
            setEmailSendResults(null);
            // Ensure main modal state is also reset when results modal is closed
            handleClose();
          }}
          size="4xl"
          scrollBehavior="inside"
          classNames={{
            base: "max-w-[85vw] w-full",
            body: "p-0"
          }}
        >
          <ModalContent>
            <ModalHeader>
              <div className="flex items-center gap-3">
                <div className="w-3 h-8 bg-moss-primary rounded-full"></div>
                <div>
                  <h3 className="text-xl font-semibold text-neutral-primary">
                    E-mail verzonden naar {emailSendResults.totalRecipients || emailSendResults.totalRetryAttempts || 0} contactpersonen
                  </h3>
                  <p className="text-sm text-neutral-secondary-mid">
                    {emailSendResults.successfulSends || emailSendResults.successfulRetries || 0} succesvol • {emailSendResults.failedSends || emailSendResults.stillFailed || 0} mislukt
                  </p>
                </div>
              </div>
            </ModalHeader>
            
            <ModalBody className="p-6">
              {/* Summary Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-status-good/10 border border-status-good/30 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="shrink-0 w-8 h-8 bg-status-good rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-neutral-primary">Succesvol verzonden</div>
                      <div className="text-2xl font-bold text-status-good">
                        {emailSendResults.successfulSends || emailSendResults.successfulRetries || 0}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-neutral-primary">Mislukt</div>
                      <div className="text-2xl font-bold text-red-600">
                        {emailSendResults.failedSends || emailSendResults.stillFailed || 0}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-neutral-100 border border-neutral-300 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="shrink-0 w-8 h-8 bg-neutral-400 rounded-full flex items-center justify-center">
                      <UsersThree className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-neutral-primary">Totaal contactpersonen</div>
                      <div className="text-2xl font-bold text-neutral-primary">
                        {emailSendResults.totalRecipients || emailSendResults.totalRetryAttempts || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Send Information */}
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-neutral-primary mb-2">Verzendgegevens</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-neutral-secondary-mid">Verzender:</span>
                    <span className="ml-2 font-medium">{emailSendResults.senderEmail}</span>
                  </div>
                  <div>
                    <span className="text-neutral-secondary-mid">Tijdstip:</span>
                    <span className="ml-2 font-medium">
                      {new Date(emailSendResults.timestamp).toLocaleString('nl-NL')}
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral-secondary-mid">Type:</span>
                    <span className="ml-2 font-medium">
                      {emailSendResults.emailType === 'retry_attempt' ? 'Opnieuw proberen' : 
                       emailSendResults.emailType === 'individual_with_entity_fields' ? 'Individueel met entiteitvelden' :
                       emailSendResults.emailType === 'individual_with_dynamic_values' ? 'Individueel met dynamische velden' :
                       emailSendResults.emailType === 'bcc_static_content' ? 'BCC statische inhoud' : 
                       emailSendResults.emailType}
                    </span>
                  </div>
                  {emailSendResults.buildingId && (
                    <div>
                      <span className="text-neutral-secondary-mid">Gebouw:</span>
                      <span className="ml-2 font-medium">{emailSendResults.buildingId}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Detailed Results */}
              <div className="space-y-4">
                <h4 className="font-medium text-neutral-primary flex items-center gap-2">
                  <EnvelopeSimple size={16} />
                  Alle contactpersonen ({emailSendResults.results?.length || 0})
                </h4>
                <p className="text-sm text-neutral-secondary-mid">
                  Hieronder zie je de status van elke contactpersoon waar de e-mail naar verzonden werd.
                </p>
                
                <div className="max-h-80 overflow-y-auto space-y-2">
                  {emailSendResults.results?.map((result: any, index: number) => {
                    const originalContact = contacts.find(c => c.id === result.recipient.contactId);
                    
                    return (
                      <div
                        key={index}
                        className={`border rounded-lg p-3 transition-all hover:shadow-sm ${
                          result.success 
                            ? 'border-status-good/30 bg-status-good/5' 
                            : 'border-red-300 bg-red-50'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {result.success ? (
                                <div className="shrink-0 w-6 h-6 bg-status-good rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              ) : (
                                <div className="shrink-0 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-neutral-primary">
                                  {result.recipient.contactName}
                                </span>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    result.success 
                                      ? 'bg-status-good/20 text-status-good font-medium' 
                                      : 'bg-red-200 text-red-700 font-medium'
                                  }`}>
                                    {result.success ? '✓ Succesvol' : '✗ Mislukt'}
                                  </span>
                                  {result.retryAttempt && (
                                    <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                      🔄 Opnieuw geprobeerd
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="text-sm text-neutral-secondary-mid space-y-1">
                              <div className="flex items-center gap-2">
                                <EnvelopeSimple size={14} />
                                <span>{result.recipient.emailAddress}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone size={14} />
                                <span>{originalContact?.phoneMobile || (originalContact as any)?.phone_mobile || 'Geen telefoonnummer'}</span>
                              </div>
                            </div>
                            
                            {!result.success && result.error && (
                              <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded-sm text-xs text-red-700">
                                <strong>Fout:</strong> {result.error}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </ModalBody>

            <ModalFooter className="border-t border-neutral-primary/10 px-6 py-4">
              <Button
                variant="light"
                onPress={() => {
                  setEmailSendResults(null);
                  handleClose(); // Reset all state when closing results modal
                }}
                className="text-neutral-secondary-mid"
              >
                Sluiten
              </Button>
              
              {((emailSendResults.failedRecipients && emailSendResults.failedRecipients.length > 0) ||
                (emailSendResults.stillFailedRecipients && emailSendResults.stillFailedRecipients.length > 0)) && (
                <Button
                  className="bg-brick-primary text-white"
                  onPress={handleRetryFailedEmails}
                  isLoading={isRetrying}
                  isDisabled={isRetrying}
                  startContent={!isRetrying && <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>}
                >
                  {isRetrying ? "Opnieuw proberen..." : 
                   `Opnieuw proberen (${
                     (emailSendResults.failedRecipients?.length || 0) + 
                     (emailSendResults.stillFailedRecipients?.length || 0)
                   })`
                  }
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Main Mass Mail Modal - only show when not showing results */}
      <Modal 
        isOpen={isOpen && !emailSendResults} 
        onClose={handleClose}
        size="5xl"
        scrollBehavior="inside"
        classNames={{
          base: "max-w-[90vw] w-full",
          body: "p-0"
        }}
      >
        <ModalContent>
        <ModalHeader>
          <div className="flex items-center gap-3">
            <div className="w-3 h-8 bg-moss-primary rounded-full"></div>
            <div>
              <h3 className="text-xl font-semibold text-neutral-primary">Mass-mail verzenden</h3>
              <p className="text-sm text-neutral-secondary-mid">
                Verstuur e-mails naar meerdere ontvangers
              </p>
            </div>
          </div>
        </ModalHeader>
        
        <ModalBody className="space-y-6 p-6">
          <div 
            ref={modalBodyRef}
            onDragOver={handleModalDragOver}
            onDragLeave={handleDragLeave}
          >
            {/* Resource and Contact Selection */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-primary">
                <Building className="inline h-4 w-4 mr-1" />
                  Gebouwen, units en contactpersonen selecteren <span className="text-danger">*</span>
              </label>
                <DirectSearchComponent
                  resourceFilter={[ResourceType.BUILDING, ResourceType.UNIT, ResourceType.CONTACT]}
                onSelect={handleAddParticipant}
                label="Geadresseerde toevoegen"
                  excludeRenovationProjects={true}
              />
            </div>

              {/* Selected Resources */}
              {selectedResources.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-neutral-primary">
                    Geselecteerde bronnen:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedResources.map((resource) => (
                      <div
                        key={`${resource.type}-${resource.resourceId}`}
                        className="flex w-fit items-center gap-2 rounded-full border border-neutral-primary/20 p-2 text-sm text-neutral-primary"
                      >
                  <div className="flex items-center gap-1">
                          {resource.type === 'building' ? (
                    <Building size={14} />
                          ) : (
                            <div className="w-3.5 h-3.5 rounded-sm border border-neutral-primary/30 flex items-center justify-center text-xs">
                              U
                  </div>
                          )}
                          <span>{resource.name}</span>
                          <span className="text-xs text-neutral-secondary-mid">
                            ({resource.contactCount} contact{resource.contactCount !== 1 ? 'en' : ''})
                          </span>
                        </div>
                        <Tooltip content={`Verwijder ${resource.type === 'building' ? 'gebouw' : 'unit'} en bijbehorende contacten`}>
                          <a
                            onClick={() => handleRemoveResource(resource)}
                      className="cursor-pointer text-status-bad"
                    >
                            <XCircle size={16} weight="duotone" />
                    </a>
                  </Tooltip>
                      </div>
                    ))}
                </div>
                {isLoadingContacts && (
                  <div className="flex items-center justify-center gap-2 text-sm font-normal">
                    <Spinner size="sm" color="success" />
                    Contactpersonen ophalen...
                  </div>
                )}
              </div>
            )}

            {/* Contact List */}
            {contacts.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UsersThree size={16} className="text-neutral-secondary-mid" />
                  <span className="text-sm font-medium text-neutral-primary">
                    Contactpersonen ({contacts.length})
                  </span>
                    <span className="text-xs text-green-600">
                      • {contacts.filter(c => hasValidEmail(c)).length} met geldig e-mailadres
                  </span>
                    {contacts.filter(c => !hasValidEmail(c)).length > 0 && (
                      <span className="text-xs text-orange-600">
                        • {contacts.filter(c => !hasValidEmail(c)).length} zonder geldig e-mailadres
                      </span>
                    )}
                  {getUsedDynamicFields().length > 0 && (
                    <span className="text-xs text-red-600">
                      • {contacts.filter(c => getMissingFields(c).length > 0).length} met ontbrekende velden
                    </span>
                  )}
                </div>
                  <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-h-40 overflow-y-auto">
                  {contacts.map((contact) => {
                    const missingFields = getMissingFields(contact);
                    const hasMissingFields = missingFields.length > 0;
                      const hasEmail = hasValidEmail(contact);
                      const buildingColorClass = getBuildingColor(contact);
                    
                    return (
                                              <div
                          key={contact.id}
                          className={`flex flex-col gap-1 rounded-lg border p-1.5 text-sm transition-all hover:shadow-sm ${
                            hasMissingFields 
                              ? 'border-red-300 bg-red-50' 
                              : buildingColorClass
                          }`}
                        >
                          {/* Header with name and actions */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="block truncate font-medium text-current">{contact.name}</span>
                              {!hasEmail && (
                                <Tooltip content="Geen geldig e-mailadres beschikbaar" placement="top">
                                  <Warning size={14} className="text-red-500 shrink-0" weight="fill" />
                                </Tooltip>
                              )}
                            </div>
                            <Tooltip content="Verwijder uit lijst">
                              <a
                                onClick={() => handleRemoveContact(contact.id)}
                                className="cursor-pointer text-red-500 hover:text-red-700 shrink-0"
                              >
                                <XCircle size={14} weight="duotone" />
                              </a>
                            </Tooltip>
                          </div>

                          {/* Units info */}
                          {contact.units.length > 0 && (
                            <div className="flex flex-wrap gap-1 text-xs">
                              {(() => {
                                // Group units by building
                                const groupedUnits = contact.units.reduce((groups: { [buildingId: string]: ContactUnit[] }, unit) => {
                                  if (!groups[unit.buildingId]) {
                                    groups[unit.buildingId] = [];
                                  }
                                  groups[unit.buildingId].push(unit);
                                  return groups;
                                }, {});

                                return Object.entries(groupedUnits).flatMap(([buildingId, units]) => [
                                  // Building ID chip
                                  <span key={`building-${buildingId}`} className="bg-white/70 border border-current/30 px-1.5 py-0.5 rounded-sm text-xs font-medium">
                                    {buildingId}
                                  </span>,
                                  // Unit chips for this building
                                                                      ...units.map((unit) => (
                                      <span key={unit.unitId} className="bg-white/50 border border-current/20 px-1.5 py-0.5 rounded-sm text-xs">
                                        {unit.id} {unit.referencePlus ? ` (${unit.referencePlus})` : ''}
                                      </span>
                                    ))
                                ]);
                              })()}
                            </div>
                          )}

                                                     {/* Contact details */}
                           <div className="space-y-0">
                        {contact.email && (
                              <div className={`text-xs truncate flex items-center gap-1 ${
                                hasMissingFields ? 'text-red-600' : 'text-current opacity-75'
                              }`}>
                                <EnvelopeSimple size={10} />
                            {contact.email}
                          </div>
                        )}
                            {contact.phoneMobile && contact.phoneMobile !== 'GSM-nummer onbekend' && (
                              <div className={`flex items-center gap-1 text-xs ${
                                hasMissingFields ? 'text-red-600' : 'text-current opacity-75'
                              }`}>
                                <Phone size={10} />
                            {contact.phoneMobile}
                          </div>
                        )}
                          </div>

                                                     {/* Missing fields warning */}
                           {hasMissingFields && (
                             <div className="text-xs text-red-600 font-medium bg-red-100/50 rounded-sm px-1.5 py-0.5 mt-0.5">
                               Ontbrekende velden: {missingFields.map(f => f.replace(/[{}]/g, '')).join(', ')}
                             </div>
                           )}
                    </div>
                  );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Template Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-primary">
                Template Type
              </label>
              <Select
                placeholder="Selecteer een type"
                selectedKeys={selectedType ? [selectedType] : []}
                onChange={(e) => setSelectedType(e.target.value)}
                items={templateTypes.map((type) => ({
                  key: type.name,
                  label: type.name
                }))}
                classNames={{
                  trigger: "border-neutral-primary/20 hover:border-moss-primary"
                }}
              >
                {(type) => (
                  <SelectItem key={type.key}>
                    {type.label}
                  </SelectItem>
                )}
              </Select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-primary">
                Template
              </label>
              <Select
                placeholder={selectedType ? "Selecteer een template" : "Kies eerst een type"}
                selectedKeys={selectedTemplate ? [selectedTemplate] : []}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                isDisabled={!selectedType || filteredTemplates.length === 0}
                items={filteredTemplates.map((template) => ({
                  key: template.id?.toString() || '',
                  label: template.subject
                }))}
                classNames={{
                  trigger: "border-neutral-primary/20 hover:border-moss-primary"
                }}
              >
                {(template) => (
                  <SelectItem key={template.key}>
                    {template.label}
                  </SelectItem>
                )}
              </Select>
            </div>
          </div>

          {/* Sender Email and Subject */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-primary">
                Verzenden vanaf <span className="text-danger">*</span>
              </label>
              <Select
                selectedKeys={[senderEmail]}
                onChange={(e) => {
                  setSenderEmail(e.target.value);
                  if (e.target.value === "noreply@yally.be") {
                    setEmailContent(`${emailContent}\n\nDeze mail is automatisch verstuurd. Gelieve niet te antwoorden.`); 
                  }
                }}
                items={[
                  { key: "noreply@yally.be", label: "noreply@yally.be" },
                  { key: "ella@yally.be", label: "ella@yally.be" },
                  { key: "rent@yally.be", label: "rent@yally.be" }
                ]}
                classNames={{
                  trigger: "border-neutral-primary/20 hover:border-moss-primary"
                }}
              >
                {(item) => (
                  <SelectItem key={item.key}>
                    {item.label}
                  </SelectItem>
                )}
              </Select>
            </div>


            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-primary">
                Onderwerp <span className="text-danger">*</span>
              </label>
              <Input
                ref={subjectInputRef}
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Voer het onderwerp in"
                onDrop={handleSubjectDrop}
                onDragOver={handleSubjectDragOver}
                onDragLeave={handleDragLeave}
                classNames={{
                  inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
                }}
                description="Sleep dynamische velden hierheen of typ ze handmatig"
              />
            </div>
          </div>

          {/* File Attachments Section */}
          <div className="space-y-4 mt-8">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-neutral-primary flex items-center gap-2">
                <Paperclip size={16} />
                Bijlagen toevoegen
                {attachedFiles.length > 0 && (
                  <span className="text-xs bg-moss-primary/10 text-moss-primary px-2 py-1 rounded-full">
                    {attachedFiles.length} bestand{attachedFiles.length !== 1 ? 'en' : ''}
                  </span>
                )}
              </label>
              <div className="flex items-center gap-2">
                {attachedFiles.length > 0 && (
                  <span className="text-xs text-neutral-secondary-mid">
                    Totaal: {formatFileSize(getTotalAttachmentSize())}
                  </span>
                )}
                <Button
                  size="sm"
                  variant="bordered"
                  startContent={<UploadSimple size={16} />}
                  onPress={() => fileInputRef.current?.click()}
                  className="border-moss-primary text-moss-primary hover:bg-moss-primary hover:text-white"
                >
                  Bestanden selecteren
                </Button>
              </div>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={FILE_INPUT_ACCEPT}
              onChange={handleFileInputChange}
              className="hidden"
            />

            {/* Drag and Drop Area */}
            <div
              onDrop={handleFileDrop}
              onDragOver={handleFileDragOver}
              onDragLeave={handleFileDragLeave}
              className={`border-2 border-dashed rounded-lg p-6 transition-all ${
                isDragOver
                  ? 'border-moss-primary bg-moss-primary/5'
                  : 'border-neutral-primary/20 hover:border-moss-primary/50'
              }`}
            >
              <div className="text-center">
                <UploadSimple 
                  size={32} 
                  className={`mx-auto mb-2 ${isDragOver ? 'text-moss-primary' : 'text-neutral-secondary-mid'}`} 
                />
                <p className={`text-sm font-medium mb-1 ${isDragOver ? 'text-moss-primary' : 'text-neutral-primary'}`}>
                  {isDragOver ? 'Laat bestanden los om toe te voegen' : 'Sleep bestanden hierheen of klik op "Bestanden selecteren"'}
                </p>
                <p className="text-xs text-neutral-secondary-mid">
                  Toegestaan: PDF, Word, Excel, PowerPoint, afbeeldingen, tekstbestanden, archieven
                  <br />
                  Max 25MB per bestand, 100MB totaal
                </p>
              </div>
            </div>

            {/* Attached Files List */}
            {attachedFiles.length > 0 && (
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-neutral-primary">Toegevoegde bestanden:</h5>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {attachedFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 p-3 bg-neutral-50 border border-neutral-200 rounded-lg"
                    >
                      {/* File Icon/Preview */}
                      <div className="shrink-0">
                        {file.previewUrl ? (
                          <img
                            src={file.previewUrl}
                            alt={file.name}
                            className="w-10 h-10 object-cover rounded-sm border border-neutral-300"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-neutral-200 rounded-sm border border-neutral-300 flex items-center justify-center">
                            <File size={20} className="text-neutral-600" />
                          </div>
                        )}
                      </div>

                      {/* File Info */}
                      <div className="grow min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-neutral-primary truncate">
                            {file.name}
                          </span>
                          <span className="text-xs text-neutral-secondary-mid shrink-0">
                            {formatFileSize(file.size)}
                          </span>
                        </div>
                        <div className="text-xs text-neutral-secondary-mid">
                          {file.type || 'Onbekend type'}
                        </div>
                        
                        {/* Upload Progress */}
                        {file.isUploading && (
                          <div className="mt-2">
                            <Progress
                              size="sm"
                              value={file.uploadProgress || 0}
                              color="success"
                              className="max-w-full"
                            />
                          </div>
                        )}
                        
                        {/* Upload Error */}
                        {file.uploadError && (
                          <div className="text-xs text-red-600 mt-1">
                            Fout: {file.uploadError}
                          </div>
                        )}
                      </div>

                      {/* Remove Button */}
                      <div className="shrink-0">
                        <Tooltip content="Bestand verwijderen">
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            color="danger"
                            onPress={() => handleRemoveFile(file.id)}
                            className="min-w-0 w-8 h-8"
                          >
                            <Trash size={16} />
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Attachment Summary */}
                <div className="flex items-center justify-between text-xs text-neutral-secondary-mid bg-neutral-50 rounded-sm p-2">
                  <span>
                    {attachedFiles.length} bestand{attachedFiles.length !== 1 ? 'en' : ''} geselecteerd
                  </span>
                  <span>
                    Totale grootte: {formatFileSize(getTotalAttachmentSize())} / 100MB
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Email Content with Live Preview and Drag Fields */}
          <div className="space-y-2 mt-8">
            <label className="block text-sm font-medium text-neutral-primary">
              E-mail inhoud <span className="text-danger">*</span>
            </label>
            
              {/* Missing Entity Types Warning & Parameter Fill Reminder */}
            {(() => {
              const missingInfo = getMissingEntityTypes();
                const hasUnfilledParameters = parameters.some(param => {
                  if (param.type === 'global') {
                    return !(parameterValues[param.id] as string)?.trim();
                  } else {
                    const individualValues = parameterValues[param.id] as { [contactId: string]: string } || {};
                    if (contacts.length === 0) {
                      return !individualValues['default']?.trim();
                    }
                    return contacts.some(contact => !individualValues[contact.id]?.trim());
                  }
                });

                const showEntitiesWarning = missingInfo.missingTypes.length > 0;
                const showParametersReminder = hasUnfilledParameters && parameters.length > 0;

                return (showEntitiesWarning || showParametersReminder) && (
                  <div className={`border rounded-lg p-3 mb-4 ${showEntitiesWarning ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-start gap-2">
                    <div className="shrink-0">
                        <svg className={`w-4 h-4 mt-0.5 ${showEntitiesWarning ? 'text-red-600' : 'text-blue-600'}`} fill="currentColor" viewBox="0 0 20 20">
                          {showEntitiesWarning ? (
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          ) : (
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          )}
                      </svg>
                    </div>
                    <div className="grow">
                        {showEntitiesWarning && (
                          <>
                      <h4 className="font-medium text-red-900 mb-1">Ontbrekende gegevens</h4>
                      <p className="text-sm text-red-800 mb-2">
                        Je sjabloon bevat dynamische velden die nog niet zijn toegevoegd:
                      </p>
                      
                      {/* Missing Entities */}
                        <div className="mb-3">
                          <p className="text-xs font-medium text-red-900 mb-1">Ontbrekende entiteiten:</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {missingInfo.missingTypes.map((type, index) => (
                              <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {type}
                              </span>
                            ))}
                          </div>
                        </div>

                            <p className="text-sm text-red-800">
                              <strong>Actie vereist:</strong> Voeg deze gegevens toe via de knop "Gegevens toevoegen" hieronder,
                              anders worden de dynamische velden niet vervangen in je e-mails.
                            </p>
                          </>
                        )}

                        {showParametersReminder && !showEntitiesWarning && (
                          <>
                            <h4 className="font-medium text-blue-900 mb-1">Variabele velden invullen</h4>
                            <p className="text-sm text-blue-800 mb-2">
                              Je sjabloon bevat variabele velden die automatisch zijn gedetecteerd.
                              Vul deze in voordat je de mass-mail verstuurt.
                            </p>
                            <p className="text-sm text-blue-800">
                              <strong>Scroll naar beneden</strong> naar de sectie "Variabele velden aanmaken" om de waarden in te vullen.
                            </p>
                          </>
                        )}
                    </div>
                  </div>
                </div>
              );
            })()}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-secondary-mid">Editor</span>
                  <span className="text-xs text-neutral-secondary-mid italic">
                    Gebruik {'{Voornaam}'}, {'{Achternaam}'}, {'{Email}'}, etc. voor dynamische gegevens
                  </span>
                </div>
                <div 
                  className="border border-neutral-primary/20 rounded-lg overflow-hidden"
                  onDrop={handleEditorDrop}
                  onDragOver={handleEditorDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={emailContent}
                    onChange={setEmailContent}
                    modules={modules}
                    placeholder="Typ de inhoud van je e-mail hier... Of sleep dynamische velden hierheen"
                    style={{ 
                      height: '300px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
                <div className="h-12"></div>
              </div>
              
              {/* Live Preview */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-secondary-mid">Live Voorbeeld</span>
                  <div className="flex items-center gap-2">
                    {/* Preview Contact Selector */}
                    <Select
                      size="sm"
                      selectedKeys={[String(getSelectedPreviewContact().id)]}
                      onChange={(e) => setSelectedPreviewContactId(e.target.value)}
                      placeholder="Kies preview contact"
                      className="min-w-[150px]"
                      classNames={{
                        trigger: "h-6 min-h-0 border-neutral-primary/20 hover:border-moss-primary"
                      }}
                      items={contacts.length > 0 ? contacts : [dummyContact]}
                    >
                      {(contact) => (
                        <SelectItem key={contact.id}>
                          {contact.name}
                        </SelectItem>
                      )}
                    </Select>
                  </div>
                </div>
                <div className="border border-neutral-primary/20 rounded-lg bg-white min-h-[344px] max-h-[344px] overflow-y-auto">
                  <div className="p-4">
                    {/* Subject Preview */}
                    {emailSubject && (
                      <div className="mb-4 pb-2 border-b border-neutral-primary/10">
                        <span className="text-xs text-neutral-secondary-mid">Onderwerp:</span>
                        <p className="font-medium text-neutral-primary">{replacePreviewDynamicValues(emailSubject, false)}</p>
                      </div>
                    )}
                    
                    {/* Content Preview with Header/Footer */}
                    <div className="prose prose-sm max-w-none">
                      {emailContent || emailSettings?.headerHtml || emailSettings?.footerHtml ? (
                        <div dangerouslySetInnerHTML={{ 
                          __html: replacePreviewDynamicValues(`
                            ${emailSettings?.headerHtml || ''}
                            ${emailContent || ''}
                            ${applyFooterSender(emailSettings?.footerHtml, senderEmail)}
                          `)
                        }} />
                      ) : (
                        <p className="text-neutral-secondary-mid italic">
                          Typ inhoud om een voorbeeld te zien...
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Drag and Drop Fields Column */}
              <div className="space-y-2">
                <span className="text-xs text-neutral-secondary-mid">Sleep deze velden</span>
                <div className="border border-neutral-primary/20 rounded-lg bg-gray-50 p-3 max-h-[344px] overflow-y-auto">
                  <div className="space-y-3">
                    {/* Contact Fields */}
                    <div className="space-y-2">
                      <h5 className="text-xs font-medium text-neutral-primary flex items-center gap-1">
                        <UsersThree size={12} className="text-blue-600" />
                        Geadresseerde velden
                      </h5>
                      <div className="space-y-1">
                        {dynamicFields.map((field) => (
                          <div
                            key={field.key}
                            draggable
                            onDragStart={(e) => handleDragStart(e, field.placeholder)}
                            onDragEnd={handleDragEnd}
                            className="flex items-center gap-2 p-2 bg-white border border-neutral-primary/20 rounded-sm cursor-grab active:cursor-grabbing hover:border-moss-primary hover:shadow-sm transition-all group text-xs"
                            title={`Sleep naar editor om ${field.placeholder} in te voegen (wordt: ${field.value})`}
                          >
                            <DotsSixVertical size={10} className="text-neutral-secondary-mid group-hover:text-moss-primary" />
                            <div className="grow min-w-0">
                              <code className="text-xs font-mono bg-neutral-100 px-1 rounded-sm text-neutral-primary">
                                {field.placeholder}
                              </code>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Parameter Fields */}
                    {parameters.length > 0 && (
                      <div className="space-y-2">
                        <h5 className="text-xs font-medium text-neutral-primary flex items-center gap-1">
                          <svg className="w-3 h-3 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                          </svg>
                          Variabele velden
                        </h5>
                        <div className="space-y-1">
                          {parameters.map((parameter) => (
                            <div
                              key={parameter.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, parameter.placeholder)}
                              onDragEnd={handleDragEnd}
                              className="flex items-center gap-2 p-2 bg-white border border-purple-200 rounded-sm cursor-grab active:cursor-grabbing hover:border-purple-400 hover:shadow-sm transition-all group text-xs"
                              title={`Sleep naar editor om ${parameter.placeholder} in te voegen (${parameter.type === 'global' ? 'Globaal' : 'Individueel'})`}
                            >
                              <DotsSixVertical size={10} className="text-purple-600 group-hover:text-purple-700" />
                              <div className="grow min-w-0">
                                <code className="text-xs font-mono bg-purple-50 px-1 rounded-sm text-purple-700">
                                  {parameter.placeholder}
                                </code>
                                <div className="text-xs text-neutral-secondary-mid truncate mt-1">
                                  {parameter.name} ({parameter.type === 'global' ? 'Globaal' : 'Individueel'})
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Entity Fields */}
                    {Object.keys(getEntityDynamicFields).length > 0 && (
                      <div className="space-y-2">
                        {Object.entries(getEntityDynamicFields).map(([entityId, entityGroup]: [string, any]) => (
                          <div key={entityId} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <h5 className="text-xs font-medium text-neutral-primary flex items-center gap-1">
                                <Database size={10} className="text-moss-primary" />
                                {entityGroup.entityName}
                              </h5>
                              <Button
                                size="sm"
                                variant="light"
                                color="danger"
                                onPress={() => handleRemoveMentionedEntity(entityId)}
                                className="min-w-0 px-1 h-5 text-xs"
                              >
                                ×
                              </Button>
                            </div>
                            <div className="space-y-1">
                              {entityGroup.fields.map((field: any) => (
                                <div
                                  key={field.key}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, field.placeholder)}
                                  onDragEnd={handleDragEnd}
                                  className="flex items-center gap-2 p-2 bg-white border border-moss-primary/20 rounded-sm cursor-grab active:cursor-grabbing hover:border-moss-primary hover:shadow-sm transition-all group text-xs"
                                  title={`Sleep naar editor om ${field.placeholder} in te voegen (wordt: ${field.value})`}
                                >
                                  <DotsSixVertical size={10} className="text-moss-primary group-hover:text-moss-primary" />
                                  <div className="grow min-w-0">
                                    <code className="text-xs font-mono bg-moss-primary/10 px-1 rounded-sm text-moss-primary">
                                      {field.placeholder}
                                    </code>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>



            {/* Required Entities Section */}
            {(() => {
              const requiredEntityInstances = getRequiredEntityInstances();

              if (requiredEntityInstances.length === 0) return null;

              const entityTypeLabels: { [key: string]: string } = {
                'company': 'Bedrijf',
                'building': 'Gebouw',
                'unit': 'Unit',
                'helpdesk': 'Helpdesk Ticket',
                'task': 'Taak',
                'contract': 'Contract',
                'contact': 'Contact'
              };

              return (
                <div className="space-y-4 border-t border-neutral-primary/10 pt-6">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <h4 className="text-lg font-medium text-neutral-primary">Vereiste gegevens voor deze sjabloon</h4>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800 mb-4">
                      Deze sjabloon vereist de volgende specifieke entiteiten. Selecteer voor elke entiteit de juiste gegevens:
                    </p>
                    <div className="space-y-4">
                      {requiredEntityInstances.map((entityInstance) => {
                        // Extract type from instance (e.g., "company" from "company1")
                        const entityType = entityInstance.replace(/\d+$/, '');
                        const label = entityTypeLabels[entityType] || entityType;

                        // Check if this specific instance is already added
                        const alreadyAdded = mentionedEntities.some(e => e.uniqueId === entityInstance);

                        return (
                          <div key={entityInstance} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="block text-sm font-medium text-neutral-primary">
                                <Database size={16} className="inline mr-2" />
                                {label} selecteren ({entityInstance})
                              </label>
                              {alreadyAdded && (
                                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                                  ✓ Geselecteerd
                                </span>
                              )}
                            </div>
                            {!alreadyAdded ? (
                              <SearchComponent
                                customEndpoint="/email/search-entities"
                                customDataTransform={transformEmailSearchResults}
                                resourceFilter={[entityType as ResourceType]}
                                onSelect={(resource: any) => {
                                  // Create a modified version of the resource with the specific uniqueId
                                  const modifiedResource = {
                                    ...resource,
                                    originalUniqueId: entityInstance // Store the required uniqueId
                                  };
                                  handleAddMentionedEntity(modifiedResource);
                                }}
                                label={`Zoek ${label.toLowerCase()} voor ${entityInstance}`}
                                classNames="w-full"
                              />
                            ) : (
                              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  <span className="text-sm text-green-800">
                                    {label} ({entityInstance}) is geselecteerd. Controleer hieronder bij "Toegevoegde entiteiten".
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* Add Entity Section */}
          <div className="space-y-4 border-t border-neutral-primary/10 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database size={20} className="text-neutral-secondary-mid" />
                  <h4 className="text-lg font-medium text-neutral-primary">Extra gegevens toevoegen</h4>
              </div>
              <Button
                variant="bordered"
                size="sm"
                startContent={<Plus size={16} />}
                onPress={() => setShowAddEntity(!showAddEntity)}
                className="border-moss-primary text-moss-primary hover:bg-moss-primary hover:text-white"
              >
                  Extra gegevens toevoegen
              </Button>
            </div>

            {/* Show mentioned entities */}
            {mentionedEntities.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-neutral-secondary-mid">
                  Toegevoegde entiteiten (gebruik deze in uw email als dynamische velden):
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {mentionedEntities.map((entity) => (
                    <div
                      key={entity.uniqueId}
                      className="flex items-center justify-between bg-neutral-primaryLight/5 rounded-lg p-3 border border-neutral-primary/10"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Database size={16} className="text-moss-primary" />
                          <span className="font-medium text-neutral-primary">{entity.name}</span>
                          <span className="text-xs bg-moss-primary/10 text-moss-primary px-2 py-1 rounded-full">
                            {entity.uniqueId}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="light"
                        size="sm"
                        color="danger"
                        onPress={() => handleRemoveMentionedEntity(entity.uniqueId)}
                      >
                        <XCircle size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Entity Search */}
            {showAddEntity && (
              <div className="space-y-3 p-4 bg-neutral-primaryLight/5 rounded-lg border border-neutral-primary/10">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-neutral-primary">
                    Zoek naar entiteiten om te vermelden in uw email
                  </p>
                  <Button
                    variant="light"
                    size="sm"
                    onPress={() => setShowAddEntity(false)}
                  >
                    <XCircle size={16} />
                  </Button>
                </div>
                <SearchComponent
                  customEndpoint="/email/search-entities"
                  customDataTransform={transformEmailSearchResults}
                  resourceFilter={['contact', 'company', 'building', 'unit', 'helpdesk', 'task', 'contract'] as ResourceType[]}
                  onSelect={handleAddMentionedEntity}
                  label="Zoek op entiteitnaam, type of veld"
                  classNames="w-full"
                />
                <p className="text-xs text-neutral-secondary-mid">
                    Tip: Voeg hier extra entiteiten toe die niet vereist zijn voor de sjabloon maar die u wel wilt gebruiken.
                </p>
              </div>
            )}
          </div>

          {/* Parameter Creation Section */}
          <div className="space-y-4 border-t border-neutral-primary/10 pt-6 mt-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-neutral-secondary-mid" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                <h4 className="text-lg font-medium text-neutral-primary">Variabele velden aanmaken</h4>
              </div>
              <Button
                variant="bordered"
                size="sm"
                startContent={<Plus size={16} />}
                onPress={() => setShowCreateParameter(!showCreateParameter)}
                className="border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white"
              >
                Variabel veld toevoegen
              </Button>
            </div>
            
            {showCreateParameter && (
              <div className="space-y-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-primary">
                      Variabel veld naam
                    </label>
                    <Input
                      value={newParameterName}
                      onChange={(e) => setNewParameterName(e.target.value)}
                      placeholder="Bijv. Deadline, Locatie, Bedrag"
                      size="sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-primary">
                      Type
                    </label>
                    <Select
                      selectedKeys={[newParameterType]}
                      onChange={(e) => setNewParameterType(e.target.value as 'global' | 'individual')}
                      size="sm"
                      items={[
                        { key: "global", label: "Globaal (zelfde waarde voor iedereen)" },
                        { key: "individual", label: "Individueel (unieke waarde per ontvanger)" }
                      ]}
                    >
                      {(item) => (
                        <SelectItem key={item.key}>
                          {item.label}
                        </SelectItem>
                      )}
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-purple-600 text-white"
                    onPress={handleCreateParameter}
                    isDisabled={!newParameterName.trim()}
                  >
                    Variabel veld aanmaken
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() => {
                      setShowCreateParameter(false);
                      setNewParameterName('');
                    }}
                  >
                    Annuleren
                  </Button>
                </div>
              </div>
            )}

            {/* Parameter Value Inputs */}
            {parameters.length > 0 && (
              <div className="space-y-4">
                <h5 className="text-sm font-medium text-neutral-primary">Variabel veld waarden:</h5>
                {parameters.map((parameter) => (
                  <div key={parameter.id} className="space-y-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono bg-purple-100 px-2 py-1 rounded-sm text-purple-700">
                          {parameter.placeholder}
                        </code>
                        <span className="text-sm font-medium text-neutral-primary">{parameter.name}</span>
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                          {parameter.type === 'global' ? 'Globaal' : 'Individueel'}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="light"
                        color="danger"
                        onPress={() => handleRemoveParameter(parameter.id)}
                        className="min-w-0 px-2"
                      >
                        ×
                      </Button>
                    </div>
                    
                    {parameter.type === 'global' ? (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-neutral-primary">
                          Waarde voor alle ontvangers:
                        </label>
                        <Input
                          value={parameterValues[parameter.id] as string || ''}
                          onChange={(e) => handleParameterValueChange(parameter.id, e.target.value)}
                          placeholder={`Voer waarde in voor ${parameter.name}`}
                          size="sm"
                        />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-neutral-primary">
                          Individuele waarden per ontvanger:
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {contacts.length > 0 ? contacts.map((contact) => (
                            <div key={contact.id} className="space-y-1">
                              <label className="block text-xs font-medium text-neutral-primary">
                                {contact.name}
                              </label>
                              <Input
                                  value={(parameterValues[parameter.id] as { [contactId: string]: string })?.[contact.id] || ''}
                                onChange={(e) => handleParameterValueChange(parameter.id, e.target.value, contact.id)}
                                placeholder={`${parameter.name} voor ${contact.name}`}
                                size="sm"
                              />
                            </div>
                          )) : (
                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-neutral-primary">
                                Standaard waarde (geen contacten geselecteerd)
                              </label>
                              <Input
                                  value={(parameterValues[parameter.id] as { [contactId: string]: string })?.['default'] || ''}
                                onChange={(e) => handleParameterValueChange(parameter.id, e.target.value, 'default')}
                                placeholder={`${parameter.name} waarde`}
                                size="sm"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-8">
            <div className="flex items-start gap-3">
              <div className="shrink-0">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="grow">
                <h4 className="font-medium text-blue-900 mb-2">Dynamische gegevens gebruiken</h4>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>
                    <strong>Sleep de velden</strong> naar het onderwerp of de inhoud om automatisch contactgegevens in te vullen. 
                    Je kunt ook handmatig typen zoals <code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{Voornaam}'}</code>.
                  </p>
                  <p>
                    <strong>Preview selecteren:</strong> Kies in het dropdown menu boven het preview venster welk contact 
                    je wilt gebruiken voor de voorvertoning. Je kunt kiezen uit echte contactpersonen of de dummy data.
                  </p>
                  <p>
                    <strong>Ontbrekende gegevens:</strong> Contactpersonen waarbij belangrijke informatie ontbreekt 
                    voor je dynamische velden verschijnen in het <span className="text-red-600 font-medium">rood</span> met 
                    details over welke velden missen.
                  </p>
                  <p>
                    <strong>Gegevens velden:</strong> Voeg gegevens toe om hun velden als dynamische data te gebruiken 
                    (bijv. <code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{building1.naam}'}</code>).
                  </p>
                  <p>
                    <strong>Variabele velden:</strong> Maak aangepaste variabele velden aan die <span className="bg-purple-100 px-1 rounded-sm text-purple-700 font-medium">paars gemarkeerd</span> worden. 
                    Globale variabele velden hebben dezelfde waarde voor iedereen, individuele variabele velden zijn uniek per ontvanger.
                  </p>
                  <p>
                    <strong>Bijlagen:</strong> Voeg bestanden toe die als bijlage meegestuurd worden met elke e-mail. 
                    Ondersteunde formaten: PDF, Word, Excel, PowerPoint, afbeeldingen, tekstbestanden en archieven. 
                    Maximum 25MB per bestand, 100MB totaal.
                  </p>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <MagnifyingGlass size={16} className="text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Beschikbare dynamische velden:</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{Voornaam}'}</code> = {getSelectedPreviewContact().firstName}</div>
                    <div><code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{Achternaam}'}</code> = {getSelectedPreviewContact().lastName}</div>
                    <div><code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{VolledigeNaam}'}</code> = {getSelectedPreviewContact().name}</div>
                    <div><code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{Email}'}</code> = {getSelectedPreviewContact().email}</div>
                    <div><code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{MobielNummer}'}</code> = {getSelectedPreviewContact().phoneMobile}</div>
                    <div><code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{ResourceID}'}</code> = {getSelectedPreviewContact().resourceId}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </ModalBody>

        <ModalFooter className="border-t border-neutral-primary/10 px-6 py-4">
          <Button
            variant="light"
            onPress={handleClose}
            className="text-neutral-secondary-mid"
          >
            Annuleren
          </Button>
          <Button
            className="bg-moss-primary text-white"
            onPress={handleSendMassEmail}
            isLoading={isSending}
            isDisabled={(() => {
              const hasContacts = contacts.length > 0;
              const hasSubject = !!emailSubject.trim();
              const hasContent = !!emailContent.trim();
              const hasValidEmails = contacts.filter(c => hasValidEmail(c)).length > 0;
              
              const isDisabled = !hasContacts || !hasSubject || !hasContent || !hasValidEmails;
              
              console.log('Send button validation:', {
                hasContacts,
                hasSubject,
                hasContent,
                hasValidEmails,
                isDisabled
              });
              
              return isDisabled;
            })()}
            startContent={!isSending && <EnvelopeSimple />}
          >
            {isSending 
              ? "Verzenden..." 
              : `Mass-mail versturen${contacts.length > 0 ? ` (${contacts.filter(c => hasValidEmail(c)).length}${attachedFiles.length > 0 ? ` + ${attachedFiles.length} bijlage${attachedFiles.length !== 1 ? 'n' : ''}` : ''})` : ''}`
            }
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
}; 